import React, { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Heading,
  Text,
  VStack,
  HStack,
  Card,
  CardBody,
  Badge,
  Icon,
  Flex,
  Input,
  InputGroup,
  InputLeftElement,
  InputRightElement,
  useColorModeValue,
  Button,
  Avatar,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
  Tag,
  TagLabel,
  TagLeftIcon,
  Wrap,
  WrapItem,
  SimpleGrid,
  Divider,
  IconButton,
  Tooltip,
  Image,
  Skeleton,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Spinner,
} from '@chakra-ui/react';
import {
  LuShield,
  LuSearch,
  LuUsers,
  LuDatabase,
  LuPlus,
  LuCheck,
  LuMapPin,
  LuBuilding2,
  LuMail,
  LuUserPlus,
  LuChevronRight,
  LuHash,
  LuLock,
  LuEye,
  LuEyeOff,
  LuSparkles,
  LuSettings,
  LuUpload,
  LuX,
  LuPenLine,
  LuImage,
  LuClock,
  LuStar,
  LuTrash2,
  LuCreditCard,
  LuZap,
  LuArrowLeft,
  LuArrowRight,
  LuRocket,
  LuKeyboard,
  LuCheckCircle2,
  LuLoader,
} from 'react-icons/lu';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { updateAgencyBranding } from '../services/userService';
import {
  triggerScrape,
  getScrapeStatus,
  getScrapeDetails,
  getAgencyClients,
  addAgencyClient,
  updateAgencyClients,
  getAgencyMembers,
  createAgencyMember,
  deleteAgencyMember,
  analyzeUrl,
  discoverCompetitors,
  triggerKeywordScrape,
} from '../services/agencyService';
import ScrapeMapPanel from './ScrapeMapPanel';
import MarketSearchWizard from './MarketSearchWizard';
import { trackInitiateCheckout, trackLead } from '../utils/metaPixel';

const TOTAL_PLAN_SCRAPES = 20;
const FREE_SCRAPES = 3;


/* ─── Stat Card ─── */
const STAT_PALETTES = {
  blue:      { solid: 'blue.500',  glow: '0 8px 24px rgba(66,153,225,0.30)',  iconColor: 'white'    },
  gray:      { solid: 'gray.500',  glow: '0 8px 24px rgba(113,128,150,0.30)', iconColor: 'white'    },
  lightBlue: { solid: 'blue.300',  glow: '0 8px 24px rgba(144,205,244,0.35)', iconColor: 'white'    },
  white:     { solid: 'gray.200',  glow: '0 8px 24px rgba(203,213,224,0.25)', iconColor: 'gray.600' },
};

function StatCard({ icon, label, value, color = 'blue' }) {
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderCol = useColorModeValue('gray.100', 'gray.700');
  const valueColor = useColorModeValue('gray.900', 'white');
  const p = STAT_PALETTES[color] || STAT_PALETTES.blue;

  return (
    <Card
      bg={cardBg}
      borderWidth="1px"
      borderColor={borderCol}
      shadow="sm"
      position="relative"
      overflow="hidden"
      _hover={{
        boxShadow: p.glow,
        transform: 'translateY(-2px)',
        borderColor: p.solid,
      }}
      transition="all 0.22s cubic-bezier(0.4,0,0.2,1)"
      cursor="default"
    >
      {/* Colored top accent bar */}
      <Box position="absolute" top={0} left={0} right={0} h="3px" bg={p.solid} />
      <CardBody py={4} px={5}>
        <HStack spacing={4}>
          <Flex
            w="42px"
            h="42px"
            borderRadius="12px"
            bg={p.solid}
            justify="center"
            align="center"
            flexShrink={0}
          >
            <Icon as={icon} boxSize="18px" color={p.iconColor} />
          </Flex>
          <Box>
            <Text
              fontSize="2xl"
              fontWeight="800"
              lineHeight="1.1"
              color={valueColor}
              letterSpacing="-0.03em"
            >
              {value}
            </Text>
            <Text
              fontSize="xs"
              fontWeight="600"
              color={p.solid}
              textTransform="uppercase"
              letterSpacing="0.07em"
              mt={0.5}
            >
              {label}
            </Text>
          </Box>
        </HStack>
      </CardBody>
    </Card>
  );
}

/* ─── Section Header ─── */
function SectionHeader({ icon, title, subtitle, action }) {
  const titleColor = useColorModeValue('gray.800', 'white');
  const subColor = useColorModeValue('gray.500', 'gray.400');

  return (
    <HStack justify="space-between" align="flex-start" w="100%">
      <HStack spacing={3} align="center">
        <Flex
          w="32px"
          h="32px"
          borderRadius="8px"
          bg="blue.50"
          justify="center"
          align="center"
          flexShrink={0}
        >
          <Icon as={icon} boxSize="15px" color="blue.500" />
        </Flex>
        <Box>
          <Text
            fontSize="md"
            fontWeight="600"
            color={titleColor}
            letterSpacing="-0.01em"
          >
            {title}
          </Text>
          {subtitle && (
            <Text fontSize="xs" color={subColor} mt={0.5}>
              {subtitle}
            </Text>
          )}
        </Box>
      </HStack>
      {action}
    </HStack>
  );
}

function AgencyAdminDashboard() {
  const { vertical, isAgencyOwner, agencyVerticals, email, displayName, activeVertical, setActiveVertical, refreshUserProfile, agencyBranding } = useUser();
  const navigate = useNavigate();
  const toast = useToast();

  /* ─── Theme tokens ─── */
  const pageBg = useColorModeValue('gray.50', 'gray.900');
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const subtleBorder = useColorModeValue('gray.100', 'gray.700');
  const hoverBg = useColorModeValue('gray.50', 'gray.700');
  const subtleText = useColorModeValue('gray.500', 'gray.400');
  const headingColor = useColorModeValue('gray.800', 'white');
  const activeBg = useColorModeValue('blue.50', 'blue.900');
  const activeBorder = useColorModeValue('blue.200', 'blue.600');
  const inputBg = useColorModeValue('gray.50', 'gray.900');
  const checkoutPricingBg = useColorModeValue('blue.50', 'blue.900');
  const checkoutPriceColor = useColorModeValue('blue.700', 'blue.200');
  const checkoutFeatureCheckBg = useColorModeValue('green.100', 'green.900');

  /* ─── Data ─── */
  const scrapes = useMemo(() => {
    if (agencyVerticals && agencyVerticals.length > 0) {
      return agencyVerticals;
    }
    if (vertical) {
      return [{ id: vertical, name: vertical }];
    }
    return [];
  }, [agencyVerticals, vertical]);

  const [clients, setClients] = useState([]);
  const [scrapeStatuses, setScrapeStatuses] = useState([]);
  const [isLoadingClients, setIsLoadingClients] = useState(false);
  const [isLoadingStatuses, setIsLoadingStatuses] = useState(false);
  const [isTriggeringScrape, setIsTriggeringScrape] = useState(false);
  const [isAddingClient, setIsAddingClient] = useState(false);
  const [isSavingAssignment, setIsSavingAssignment] = useState(false);

  /* ─── Member state ─── */
  const [members, setMembers] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [isDeletingMember, setIsDeletingMember] = useState(false);
  const [memberSearch, setMemberSearch] = useState('');
  const [addMemberEmail, setAddMemberEmail] = useState('');
  const [addMemberPassword, setAddMemberPassword] = useState('');
  const [addMemberFirstName, setAddMemberFirstName] = useState('');
  const [addMemberLastName, setAddMemberLastName] = useState('');
  const [addMemberVertical, setAddMemberVertical] = useState('');
  const [addMemberTier, setAddMemberTier] = useState('Free');
  const [showMemberPassword, setShowMemberPassword] = useState(false);
  const [memberToDelete, setMemberToDelete] = useState(null);

  /* ─── Load clients and scrape statuses on mount ─── */
  useEffect(() => {
    if (!isAgencyOwner) return;

    setIsLoadingClients(true);
    getAgencyClients()
      .then((data) => setClients(data.clients || []))
      .catch((err) => {
        toast({
          title: 'Failed to load clients',
          description: err.message || 'Could not fetch client list.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right',
        });
      })
      .finally(() => setIsLoadingClients(false));
  }, [isAgencyOwner, toast]);

  useEffect(() => {
    if (!isAgencyOwner) return;

    setIsLoadingMembers(true);
    getAgencyMembers()
      .then((data) => setMembers(data.members || []))
      .catch((err) => {
        toast({
          title: 'Failed to load members',
          description: err.message || 'Could not fetch member list.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right',
        });
      })
      .finally(() => setIsLoadingMembers(false));
  }, [isAgencyOwner, toast]);

  useEffect(() => {
    if (!isAgencyOwner) return;

    setIsLoadingStatuses(true);
    getScrapeStatus()
      .then((data) => setScrapeStatuses(data.executions || []))
      .catch(() => {
        // Non-critical — statuses are supplementary
      })
      .finally(() => setIsLoadingStatuses(false));
  }, [isAgencyOwner]);

  /* ─── State ─── */
  const [activeSection, setActiveSection] = useState('scrapes');
  const [highlightAgencyName, setHighlightAgencyName] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [pendingScrape, setPendingScrape] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [scrapeSearch, setScrapeSearch] = useState('');

  const [scrapeMethod] = useState('keyword');
  const [newScrapeKeyword, setNewScrapeKeyword] = useState('');
  const [newScrapeUrl, setNewScrapeUrl] = useState('');
  const [newScrapeRegion, setNewScrapeRegion] = useState('');
  const [newScrapeNiche, setNewScrapeNiche] = useState('');
  const [isAnalyzingUrl, setIsAnalyzingUrl] = useState(false);
  const [urlAnalysis, setUrlAnalysis] = useState(null);

  const [addClientName, setAddClientName] = useState('');
  const [addClientEmail, setAddClientEmail] = useState('');
  const [addClientBusiness, setAddClientBusiness] = useState('');
  const [addClientPassword, setAddClientPassword] = useState('');
  const [showClientPassword, setShowClientPassword] = useState(false);
  const [addClientScrape, setAddClientScrape] = useState('');

  const assignModal = useDisclosure();
  const scrapeModal = useDisclosure();
  const keywordScrapeModal = useDisclosure();
  const addClientModal = useDisclosure();
  const scrapeDetailsModal = useDisclosure();
  const addMemberModal = useDisclosure();
  const deleteMemberModal = useDisclosure();
  const checkoutModal = useDisclosure();
  const openCheckoutModal = useCallback(() => {
    trackInitiateCheckout({
      content_name: 'Agency Pro Plan',
      content_category: 'agency_white_label',
      currency: 'USD',
      value: 497,
    });
    checkoutModal.onOpen();
  }, [checkoutModal]);

  /* ─── Competitor preview state ─── */
  const [scrapeStep, setScrapeStep] = useState(1);
  const [isDiscoveringCompetitors, setIsDiscoveringCompetitors] = useState(false);
  const [discoveredCompetitors, setDiscoveredCompetitors] = useState([]);
  const [pendingVerticalId, setPendingVerticalId] = useState('');
  const [pendingScrapeNiche, setPendingScrapeNiche] = useState('');
  const [pendingScrapeRegion, setPendingScrapeRegion] = useState('');
  const [anchorBusiness, setAnchorBusiness] = useState(null);
  const [marketResolution, setMarketResolution] = useState(null);

  /* ─── Keyword scrape state (mirrors review scrape state) ─── */
  const [kwScrapeMethod] = useState('keyword');
  const [kwKeyword, setKwKeyword] = useState('');
  const [kwUrl, setKwUrl] = useState('');
  const [kwNiche, setKwNiche] = useState('');
  const [kwRegion, setKwRegion] = useState('');
  const [isAnalyzingKwUrl, setIsAnalyzingKwUrl] = useState(false);
  const [kwUrlAnalysis, setKwUrlAnalysis] = useState(null);
  const [kwScrapeStep, setKwScrapeStep] = useState(1);
  const [isDiscoveringKwCompetitors, setIsDiscoveringKwCompetitors] = useState(false);
  const [discoveredKwCompetitors, setDiscoveredKwCompetitors] = useState([]);
  const [pendingKwVerticalId, setPendingKwVerticalId] = useState('');
  const [pendingKwNiche, setPendingKwNiche] = useState('');
  const [pendingKwRegion, setPendingKwRegion] = useState('');
  const [isTriggeringKwScrape, setIsTriggeringKwScrape] = useState(false);

  const [scrapeDetails, setScrapeDetails] = useState(null);
  const [isLoadingScrapeDetails, setIsLoadingScrapeDetails] = useState(false);
  const [selectedScrapeForDetails, setSelectedScrapeForDetails] = useState(null);

  const currentScrape = vertical || scrapes[0]?.id || '';

  /* ─── Computed ─── */
  const filteredClients = useMemo(() => {
    if (!clientSearch.trim()) return clients;
    const q = clientSearch.toLowerCase();
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      c.email.toLowerCase().includes(q) ||
      (c.businessName || '').toLowerCase().includes(q)
    );
  }, [clients, clientSearch]);

  const filteredMembers = useMemo(() => {
    if (!memberSearch.trim()) return members;
    const q = memberSearch.toLowerCase();
    return members.filter(m =>
      (m.email || '').toLowerCase().includes(q) ||
      (m.first_name || '').toLowerCase().includes(q) ||
      (m.last_name || '').toLowerCase().includes(q) ||
      (m.vertical || '').toLowerCase().includes(q)
    );
  }, [members, memberSearch]);

  const filteredScrapes = useMemo(() => {
    if (!scrapeSearch.trim()) return scrapes;
    const q = scrapeSearch.toLowerCase();
    return scrapes.filter(s =>
      (s.name || s.id).toLowerCase().includes(q) ||
      (s.region || '').toLowerCase().includes(q) ||
      (s.niche || '').toLowerCase().includes(q)
    );
  }, [scrapes, scrapeSearch]);

  const totalBusinesses = useMemo(
    () => scrapes.reduce((sum, s) => sum + (s.businessCount || 0), 0),
    [scrapes]
  );

  const uniqueRegions = useMemo(
    () => new Set(scrapes.map(s => s.region).filter(Boolean)).size,
    [scrapes]
  );

  /* ─── Handlers ─── */
  const handleClientClick = (client) => {
    setSelectedClient(client);
    setPendingScrape(client.assignedScrape || '');
    assignModal.onOpen();
  };

  const handleSaveAssignment = async () => {
    setIsSavingAssignment(true);
    try {
      const updatedClients = clients.map(c =>
        (c.email === selectedClient.email || c.name === selectedClient.name)
          ? { ...c, assignedScrape: pendingScrape, verticals: [pendingScrape] }
          : c
      );
      await updateAgencyClients(updatedClients);
      setClients(updatedClients);
      toast({
        title: 'Market Report updated',
        description: `${selectedClient.name}'s market report assignment has been saved.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (err) {
      toast({
        title: 'Failed to save assignment',
        description: err.message || 'Could not update client assignment.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsSavingAssignment(false);
    }
    assignModal.onClose();
  };

  const handleCloseScrapeModal = () => {
    setNewScrapeKeyword('');
    setNewScrapeUrl('');
    setNewScrapeNiche('');
    setNewScrapeRegion('');
    setUrlAnalysis(null);
    setScrapeStep(1);
    setPendingVerticalId('');
    setPendingScrapeNiche('');
    setPendingScrapeRegion('');
    setDiscoveredCompetitors([]);
    setIsDiscoveringCompetitors(false);
    setAnchorBusiness(null);
    setMarketResolution(null);
    scrapeModal.onClose();
  };

  const handleMarketSearchComplete = ({
    anchorBusiness: selectedAnchor,
    marketKeyword,
    marketLabel,
    niche,
    region,
    recommendation,
    competitors,
  }) => {
    setAnchorBusiness(selectedAnchor);
    setMarketResolution(recommendation);
    setPendingVerticalId(marketKeyword);
    setPendingScrapeNiche(niche || selectedAnchor?.primary_type_display || '');
    setPendingScrapeRegion(region || marketLabel || '');
    setDiscoveredCompetitors(competitors || []);
    setScrapeStep(2);
  };

  const handlePreviewCompetitors = async () => {
    let verticalId;

    if (scrapeMethod === 'url') {
      if (!newScrapeUrl.trim()) return;

      let analysis = urlAnalysis;
      if (!analysis?.search_keyword) {
        setIsAnalyzingUrl(true);
        try {
          analysis = await analyzeUrl(newScrapeUrl.trim());
          setUrlAnalysis(analysis);
          if (analysis.niche && !newScrapeNiche.trim()) setNewScrapeNiche(analysis.niche);
          if (analysis.location && analysis.location !== 'unknown' && !newScrapeRegion.trim()) {
            setNewScrapeRegion(analysis.location);
          }
        } catch (err) {
          toast({
            title: 'URL analysis failed',
            description: err?.response?.data?.detail || err.message || 'Could not analyze the URL.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'bottom-right',
          });
          setIsAnalyzingUrl(false);
          return;
        } finally {
          setIsAnalyzingUrl(false);
        }
      }

      verticalId = analysis?.search_keyword?.trim() || newScrapeUrl.trim();
    } else {
      verticalId = newScrapeKeyword.trim();
      if (!verticalId) return;
    }

    const effectiveNiche = newScrapeNiche.trim() || urlAnalysis?.niche || '';
    const effectiveRegion =
      newScrapeRegion.trim() ||
      (urlAnalysis?.location && urlAnalysis.location !== 'unknown' ? urlAnalysis.location : '');

    setPendingVerticalId(verticalId);
    setPendingScrapeNiche(effectiveNiche);
    setPendingScrapeRegion(effectiveRegion);
    setDiscoveredCompetitors([]);
    setScrapeStep(2);

    const canDiscover = (effectiveNiche && effectiveRegion) || verticalId;
    if (canDiscover) {
      setIsDiscoveringCompetitors(true);
      try {
        const result = (effectiveNiche && effectiveRegion)
          ? await discoverCompetitors(effectiveNiche, effectiveRegion)
          : await discoverCompetitors('', '', 20, verticalId);
        setDiscoveredCompetitors(result.competitors || []);
      } catch (_err) {
        // Non-critical — user can still proceed with the scrape
      } finally {
        setIsDiscoveringCompetitors(false);
      }
    }
  };

  const handleConfirmScrape = async () => {
    if (!pendingVerticalId) return;

    setIsTriggeringScrape(true);
    try {
      const result = await triggerScrape(pendingVerticalId, {
        niche: marketResolution ? undefined : (pendingScrapeNiche || undefined),
        region: marketResolution ? undefined : (pendingScrapeRegion || undefined),
      });
      const effectiveVertical = result?.vertical || pendingVerticalId;
      const targetsFound = result?.competitor_targets_count;

      toast({
        title: 'Market Report triggered',
        description: targetsFound
          ? `"${effectiveVertical}" market report started — ${targetsFound} competitor targets discovered.`
          : `"${effectiveVertical}" market report has been started.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });

      getScrapeStatus()
        .then((data) => setScrapeStatuses(data.executions || []))
        .catch(() => {});

      refreshUserProfile().catch(() => {});

      handleCloseScrapeModal();
    } catch (err) {
      const errDetail = err?.response?.data?.detail || err.message || '';
      const isAgencySetupError = errDetail.includes('Could not resolve agency identifier');

      if (isAgencySetupError) {
        scrapeModal.onClose();
        toast({
          position: 'bottom-right',
          duration: 10000,
          isClosable: true,
          render: ({ onClose: closeToast }) => (
            <Box
              bg="blue.500"
              color="white"
              p={4}
              borderRadius="12px"
              shadow="lg"
              maxW="340px"
            >
              <HStack justify="space-between" align="flex-start" spacing={3}>
                <HStack align="flex-start" spacing={3}>
                  <Flex
                    w="28px"
                    h="28px"
                    borderRadius="8px"
                    bg="whiteAlpha.300"
                    justify="center"
                    align="center"
                    flexShrink={0}
                    mt="1px"
                  >
                    <Icon as={LuSettings} boxSize="14px" color="white" />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" fontSize="sm" lineHeight="1.3">
                      Agency setup required
                    </Text>
                    <Text fontSize="xs" mt={1} opacity={0.85} lineHeight="1.4">
                      Add agency name to start market reports.
                    </Text>
                    <Button
                      size="xs"
                      mt={2}
                      bg="whiteAlpha.300"
                      color="white"
                      _hover={{ bg: 'whiteAlpha.400' }}
                      fontWeight="600"
                      rightIcon={<Icon as={LuArrowRight} boxSize="12px" />}
                      onClick={() => {
                        setActiveSection('settings');
                        closeToast();
                      }}
                    >
                      Go to Settings
                    </Button>
                  </Box>
                </HStack>
                <IconButton
                  size="xs"
                  icon={<Icon as={LuX} boxSize="12px" />}
                  onClick={closeToast}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  minW="auto"
                  h="auto"
                  p={1}
                  aria-label="Close"
                />
              </HStack>
            </Box>
          ),
        });
      } else {
        toast({
          title: 'Failed to trigger market report',
          description: errDetail || 'Could not start market report.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } finally {
      setIsTriggeringScrape(false);
    }
  };

  const handleCreateScrape = handleConfirmScrape;

  /* ─── Keyword Scrape Handlers (mirrors review scrape handlers) ─── */
  const handleCloseKeywordScrapeModal = () => {
    setKwKeyword('');
    setKwUrl('');
    setKwNiche('');
    setKwRegion('');
    setKwUrlAnalysis(null);
    setKwScrapeStep(1);
    setPendingKwVerticalId('');
    setPendingKwNiche('');
    setPendingKwRegion('');
    setDiscoveredKwCompetitors([]);
    setIsDiscoveringKwCompetitors(false);
    keywordScrapeModal.onClose();
  };

  const handlePreviewKwCompetitors = async () => {
    let verticalId;

    if (kwScrapeMethod === 'url') {
      if (!kwUrl.trim()) return;

      let analysis = kwUrlAnalysis;
      if (!analysis?.search_keyword) {
        setIsAnalyzingKwUrl(true);
        try {
          analysis = await analyzeUrl(kwUrl.trim());
          setKwUrlAnalysis(analysis);
          if (analysis.niche && !kwNiche.trim()) setKwNiche(analysis.niche);
          if (analysis.location && analysis.location !== 'unknown' && !kwRegion.trim()) {
            setKwRegion(analysis.location);
          }
        } catch (err) {
          toast({
            title: 'URL analysis failed',
            description: err?.response?.data?.detail || err.message || 'Could not analyze the URL.',
            status: 'error',
            duration: 4000,
            isClosable: true,
            position: 'bottom-right',
          });
          setIsAnalyzingKwUrl(false);
          return;
        } finally {
          setIsAnalyzingKwUrl(false);
        }
      }
      verticalId = analysis?.search_keyword?.trim() || kwUrl.trim();
    } else {
      verticalId = kwKeyword.trim();
      if (!verticalId) return;
    }

    const effectiveNiche = kwNiche.trim() || kwUrlAnalysis?.niche || '';
    const effectiveRegion =
      kwRegion.trim() ||
      (kwUrlAnalysis?.location && kwUrlAnalysis.location !== 'unknown' ? kwUrlAnalysis.location : '');

    setPendingKwVerticalId(verticalId);
    setPendingKwNiche(effectiveNiche);
    setPendingKwRegion(effectiveRegion);
    setDiscoveredKwCompetitors([]);
    setKwScrapeStep(2);

    const canDiscover = (effectiveNiche && effectiveRegion) || verticalId;
    if (canDiscover) {
      setIsDiscoveringKwCompetitors(true);
      try {
        const result = (effectiveNiche && effectiveRegion)
          ? await discoverCompetitors(effectiveNiche, effectiveRegion)
          : await discoverCompetitors('', '', 20, verticalId);
        setDiscoveredKwCompetitors(result.competitors || []);
      } catch (_err) {
        // Non-critical — user can still proceed
      } finally {
        setIsDiscoveringKwCompetitors(false);
      }
    }
  };

  const handleConfirmKeywordScrape = async () => {
    if (!pendingKwVerticalId) return;

    setIsTriggeringKwScrape(true);
    try {
      const result = await triggerKeywordScrape(pendingKwVerticalId, {
        niche: pendingKwNiche || undefined,
        region: pendingKwRegion || undefined,
      });
      const effectiveVertical = result?.vertical || pendingKwVerticalId;
      const targetsFound = result?.competitor_targets_count;

      toast({
        title: 'Keyword market report triggered',
        description: targetsFound
          ? `"${effectiveVertical}" keyword market report started — ${targetsFound} competitor targets discovered.`
          : `"${effectiveVertical}" keyword market report has been started.`,
        status: 'success',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });

      getScrapeStatus()
        .then((data) => setScrapeStatuses(data.executions || []))
        .catch(() => {});

      refreshUserProfile().catch(() => {});

      handleCloseKeywordScrapeModal();
    } catch (err) {
      const errDetail = err?.response?.data?.detail || err.message || '';
      const isSetupError = errDetail.includes('Could not resolve agency identifier');

      if (isSetupError) {
        keywordScrapeModal.onClose();
        toast({
          position: 'bottom-right',
          duration: 10000,
          isClosable: true,
          render: ({ onClose: closeToast }) => (
            <Box
              bg="orange.500"
              color="white"
              p={4}
              borderRadius="12px"
              shadow="lg"
              maxW="340px"
            >
              <HStack justify="space-between" align="flex-start" spacing={3}>
                <HStack align="flex-start" spacing={3}>
                  <Flex
                    w="28px"
                    h="28px"
                    borderRadius="8px"
                    bg="whiteAlpha.300"
                    justify="center"
                    align="center"
                    flexShrink={0}
                    mt="1px"
                  >
                    <Icon as={LuSettings} boxSize="14px" color="white" />
                  </Flex>
                  <Box>
                    <Text fontWeight="700" fontSize="sm" lineHeight="1.3">
                      Agency setup required
                    </Text>
                    <Text fontSize="xs" mt={1} opacity={0.85} lineHeight="1.4">
                      Please add your agency whitelabel info in Settings before triggering market reports.
                    </Text>
                    <Button
                      size="xs"
                      mt={2}
                      bg="whiteAlpha.300"
                      color="white"
                      _hover={{ bg: 'whiteAlpha.400' }}
                      fontWeight="600"
                      rightIcon={<Icon as={LuArrowRight} boxSize="12px" />}
                      onClick={() => {
                        setActiveSection('settings');
                        closeToast();
                      }}
                    >
                      Go to Settings
                    </Button>
                  </Box>
                </HStack>
                <IconButton
                  size="xs"
                  icon={<Icon as={LuX} boxSize="12px" />}
                  onClick={closeToast}
                  variant="ghost"
                  color="white"
                  _hover={{ bg: 'whiteAlpha.300' }}
                  minW="auto"
                  h="auto"
                  p={1}
                  aria-label="Close"
                />
              </HStack>
            </Box>
          ),
        });
      } else {
        toast({
          title: 'Failed to trigger keyword market report',
          description: errDetail || 'Could not start keyword market report.',
          status: 'error',
          duration: 4000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    } finally {
      setIsTriggeringKwScrape(false);
    }
  };

  const handleAddClient = async () => {
    if (!addClientEmail.trim() || !addClientPassword.trim()) return;

    setIsAddingClient(true);
    try {
      await addAgencyClient({
        name: addClientName.trim(),
        email: addClientEmail.trim(),
        password: addClientPassword.trim(),
        business_name: addClientBusiness.trim(),
        verticals: addClientScrape ? [addClientScrape] : [],
      });

      // Refresh clients list from server
      const data = await getAgencyClients();
      setClients(data.clients || []);

      toast({
        title: 'Client added',
        description: `${addClientName.trim() || addClientEmail.trim()} has been added to your agency.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      setAddClientName('');
      setAddClientEmail('');
      setAddClientBusiness('');
      setAddClientPassword('');
      setShowClientPassword(false);
      setAddClientScrape('');
      addClientModal.onClose();
    } catch (err) {
      toast({
        title: 'Failed to add client',
        description: err?.response?.data?.detail || err.message || 'Could not add client.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsAddingClient(false);
    }
  };

  const handleViewScrapeDetails = useCallback(async (scrape) => {
    setSelectedScrapeForDetails(scrape);
    setScrapeDetails(null);
    setIsLoadingScrapeDetails(true);
    scrapeDetailsModal.onOpen();

    try {
      const data = await getScrapeDetails(scrape.id);
      setScrapeDetails(data);
    } catch (err) {
      toast({
        title: 'Failed to load market report details',
        description: err?.response?.data?.detail || err.message || 'Could not fetch details.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsLoadingScrapeDetails(false);
    }
  }, [scrapeDetailsModal, toast]);

  const handleAddMember = async () => {
    if (!addMemberEmail.trim() || !addMemberPassword.trim() || !addMemberVertical) return;

    setIsAddingMember(true);
    try {
      await createAgencyMember({
        email: addMemberEmail.trim(),
        password: addMemberPassword.trim(),
        first_name: addMemberFirstName.trim(),
        last_name: addMemberLastName.trim(),
        vertical: addMemberVertical,
        tier: addMemberTier,
      });

      const data = await getAgencyMembers();
      setMembers(data.members || []);

      toast({
        title: 'Member added',
        description: `${addMemberFirstName.trim() || addMemberEmail.trim()} has been added to your agency.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      setAddMemberEmail('');
      setAddMemberPassword('');
      setAddMemberFirstName('');
      setAddMemberLastName('');
      setAddMemberVertical('');
      setAddMemberTier('Free');
      setShowMemberPassword(false);
      addMemberModal.onClose();
    } catch (err) {
      toast({
        title: 'Failed to add member',
        description: err?.response?.data?.detail || err.message || 'Could not add member.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsAddingMember(false);
    }
  };

  const handleDeleteMember = async () => {
    if (!memberToDelete) return;

    setIsDeletingMember(true);
    try {
      await deleteAgencyMember(memberToDelete.email);

      const data = await getAgencyMembers();
      setMembers(data.members || []);

      toast({
        title: 'Member removed',
        description: `${memberToDelete.email} has been removed from your agency.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      setMemberToDelete(null);
      deleteMemberModal.onClose();
    } catch (err) {
      toast({
        title: 'Failed to remove member',
        description: err?.response?.data?.detail || err.message || 'Could not remove member.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsDeletingMember(false);
    }
  };

  /* ─── Access Gate ─── */
  if (!isAgencyOwner) {
    return (
      <Flex justify="center" align="center" minH="60vh">
        <VStack spacing={5}>
          <Flex
            w="64px"
            h="64px"
            borderRadius="16px"
            bg="gray.100"
            justify="center"
            align="center"
          >
            <Icon as={LuShield} boxSize={7} color="gray.400" />
          </Flex>
          <VStack spacing={1}>
            <Heading size="md" color="gray.600" fontWeight="600">
              Access Restricted
            </Heading>
            <Text color="gray.400" fontSize="sm">
              Agency admin permissions are required to view this page.
            </Text>
          </VStack>
          <Button
            colorScheme="blue"
            size="sm"
            onClick={() => navigate('/home')}
            borderRadius="8px"
          >
            Return Home
          </Button>
        </VStack>
      </Flex>
    );
  }

  return (
    <Box bg={pageBg} minH="100vh" pb={12}>
      {/* ════════════════════════════════════════
          HEADER
         ════════════════════════════════════════ */}
      <Box
        bg="linear-gradient(135deg, #1a365d 0%, #2b6cb0 50%, #3182ce 100%)"
        px={{ base: 4, md: 8 }}
        pt={{ base: 6, md: 8 }}
        pb={{ base: 8, md: 10 }}
        position="relative"
        overflow="hidden"
      >
        <Box
          position="absolute"
          top="-60px"
          right="-40px"
          w="240px"
          h="240px"
          borderRadius="full"
          bg="whiteAlpha.100"
        />
        <Box
          position="absolute"
          bottom="-80px"
          left="20%"
          w="300px"
          h="300px"
          borderRadius="full"
          bg="whiteAlpha.50"
        />

        <Box maxW="1600px" mx="auto" position="relative" zIndex={1}>
          <VStack spacing={3} align="center">
            <Flex
              w="64px"
              h="64px"
              borderRadius="16px"
              bg="whiteAlpha.200"
              backdropFilter="blur(8px)"
              justify="center"
              align="center"
              flexShrink={0}
            >
              <Icon as={LuShield} boxSize="32px" color="white" />
            </Flex>
            <VStack spacing={1} align="center">
              <HStack spacing={3} align="center">
                <Heading
                  size="lg"
                  color="white"
                  fontWeight="700"
                  letterSpacing="-0.025em"
                >
                  Agency Admin
                </Heading>
                <Badge
                  bg="whiteAlpha.200"
                  color="white"
                  fontSize="10px"
                  fontWeight="600"
                  borderRadius="full"
                  px={2.5}
                  py={0.5}
                  textTransform="uppercase"
                  letterSpacing="0.06em"
                >
                  Dashboard
                </Badge>
              </HStack>
              <Text color="whiteAlpha.700" fontSize="sm" fontWeight="400">
                Manage market reports and client assignments
              </Text>
            </VStack>
          </VStack>
        </Box>
      </Box>

      {/* ════════════════════════════════════════
          MAIN TWO-COLUMN LAYOUT
         ════════════════════════════════════════ */}
      <Flex
        px={{ base: 4, md: 8 }}
        mt={-6}
        gap={6}
        direction={{ base: 'column', xl: 'row' }}
        align="flex-start"
        position="relative"
        zIndex={2}
      >
        {/* ── LEFT COLUMN: Stats + Nav + Content ── */}
        <Box flex={1} minW={0}>
          <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
            <StatCard icon={LuDatabase} label="Market Reports" value={scrapes.length} color="blue" />
            <StatCard icon={LuUsers} label="Clients" value={clients.length + 1} color="gray" />
            <StatCard icon={LuBuilding2} label="Businesses" value={totalBusinesses} color="lightBlue" />
            <StatCard icon={LuMapPin} label="Regions" value={uniqueRegions} color="white" />
          </SimpleGrid>

          <Box mt={8}>
            <HStack spacing={1} p={1} bg={cardBg} borderRadius="12px" borderWidth="1px" borderColor={subtleBorder} w="fit-content" shadow="sm">
              <Button
                size="sm"
                px={5}
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                letterSpacing="-0.01em"
                bg={activeSection === 'scrapes' ? 'blue.500' : 'transparent'}
                color={activeSection === 'scrapes' ? 'white' : subtleText}
                _hover={{
                  bg: activeSection === 'scrapes' ? 'blue.600' : 'gray.100',
                  color: activeSection === 'scrapes' ? 'white' : headingColor,
                }}
                onClick={() => setActiveSection('scrapes')}
                leftIcon={<Icon as={LuDatabase} boxSize="14px" />}
                transition="all 0.15s"
              >
                Market Reports
                <Badge
                  ml={2}
                  bg={activeSection === 'scrapes' ? 'whiteAlpha.300' : 'gray.100'}
                  color={activeSection === 'scrapes' ? 'white' : subtleText}
                  fontSize="10px"
                  borderRadius="full"
                  fontWeight="600"
                  minW="20px"
                  textAlign="center"
                >
                  {scrapes.length}
                </Badge>
              </Button>
              <Button
                size="sm"
                px={5}
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                letterSpacing="-0.01em"
                bg={activeSection === 'clients' ? 'blue.500' : 'transparent'}
                color={activeSection === 'clients' ? 'white' : subtleText}
                _hover={{
                  bg: activeSection === 'clients' ? 'blue.600' : 'gray.100',
                  color: activeSection === 'clients' ? 'white' : headingColor,
                }}
                onClick={() => setActiveSection('clients')}
                leftIcon={<Icon as={LuUsers} boxSize="14px" />}
                transition="all 0.15s"
              >
                Clients
                <Badge
                  ml={2}
                  bg={activeSection === 'clients' ? 'whiteAlpha.300' : 'gray.100'}
                  color={activeSection === 'clients' ? 'white' : subtleText}
                  fontSize="10px"
                  borderRadius="full"
                  fontWeight="600"
                  minW="20px"
                  textAlign="center"
                >
                  {clients.length + 1}
                </Badge>
              </Button>
              <Button
                size="sm"
                px={5}
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                letterSpacing="-0.01em"
                bg={activeSection === 'members' ? 'blue.500' : 'transparent'}
                color={activeSection === 'members' ? 'white' : subtleText}
                _hover={{
                  bg: activeSection === 'members' ? 'blue.600' : 'gray.100',
                  color: activeSection === 'members' ? 'white' : headingColor,
                }}
                onClick={() => setActiveSection('members')}
                leftIcon={<Icon as={LuUsers} boxSize="14px" />}
                transition="all 0.15s"
              >
                Members
                <Badge
                  ml={2}
                  bg={activeSection === 'members' ? 'whiteAlpha.300' : 'gray.100'}
                  color={activeSection === 'members' ? 'white' : subtleText}
                  fontSize="10px"
                  borderRadius="full"
                  fontWeight="600"
                  minW="20px"
                  textAlign="center"
                >
                  {members.length}
                </Badge>
              </Button>
              <Button
                size="sm"
                px={5}
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                letterSpacing="-0.01em"
                bg={activeSection === 'settings' ? 'blue.500' : 'transparent'}
                color={activeSection === 'settings' ? 'white' : subtleText}
                _hover={{
                  bg: activeSection === 'settings' ? 'blue.600' : 'gray.100',
                  color: activeSection === 'settings' ? 'white' : headingColor,
                }}
                onClick={() => setActiveSection('settings')}
                leftIcon={<Icon as={LuSettings} boxSize="14px" />}
                transition="all 0.15s"
              >
                Settings
              </Button>
            </HStack>
          </Box>

          <Box mt={6}>
            {activeSection === 'scrapes' && (
              <ScrapesSection
                scrapes={filteredScrapes}
                allScrapes={scrapes}
                clients={clients}
                currentScrape={currentScrape}
                activeVertical={activeVertical}
                setActiveVertical={setActiveVertical}
                scrapeSearch={scrapeSearch}
                setScrapeSearch={setScrapeSearch}
                scrapeStatuses={scrapeStatuses}
                onCreateScrape={() => {
                  if (!agencyBranding?.agency_name?.trim()) {
                    setActiveSection('settings');
                    setHighlightAgencyName(true);
                    return;
                  }
                  scrapeModal.onOpen();
                }}
                onKeywordScrape={() => {
                  if (!agencyBranding?.agency_name?.trim()) {
                    setActiveSection('settings');
                    setHighlightAgencyName(true);
                    return;
                  }
                  keywordScrapeModal.onOpen();
                }}
                onUpgrade={openCheckoutModal}
                onViewDetails={handleViewScrapeDetails}
                cardBg={cardBg}
                borderColor={borderColor}
                subtleBorder={subtleBorder}
                hoverBg={hoverBg}
                subtleText={subtleText}
                headingColor={headingColor}
                activeBg={activeBg}
                activeBorder={activeBorder}
              />
            )}
            {activeSection === 'clients' && (
              <ClientsSection
                clients={filteredClients}
                allClients={clients}
                scrapes={scrapes}
                clientSearch={clientSearch}
                setClientSearch={setClientSearch}
                onClientClick={handleClientClick}
                onAddClient={addClientModal.onOpen}
                ownerName={displayName}
                ownerEmail={email}
                currentScrape={currentScrape}
                activeVertical={activeVertical}
                setActiveVertical={setActiveVertical}
                cardBg={cardBg}
                borderColor={borderColor}
                subtleBorder={subtleBorder}
                hoverBg={hoverBg}
                subtleText={subtleText}
                headingColor={headingColor}
                activeBg={activeBg}
                activeBorder={activeBorder}
              />
            )}
            {activeSection === 'members' && (
              <MembersSection
                members={filteredMembers}
                allMembers={members}
                memberSearch={memberSearch}
                setMemberSearch={setMemberSearch}
                onAddMember={addMemberModal.onOpen}
                onDeleteMember={(member) => {
                  setMemberToDelete(member);
                  deleteMemberModal.onOpen();
                }}
                ownerEmail={email}
                isLoading={isLoadingMembers}
                cardBg={cardBg}
                borderColor={borderColor}
                subtleBorder={subtleBorder}
                hoverBg={hoverBg}
                subtleText={subtleText}
                headingColor={headingColor}
              />
            )}
            {activeSection === 'settings' && (
              <SettingsSection
                cardBg={cardBg}
                borderColor={borderColor}
                subtleBorder={subtleBorder}
                hoverBg={hoverBg}
                subtleText={subtleText}
                headingColor={headingColor}
                inputBg={inputBg}
                highlightAgencyName={highlightAgencyName}
                onHighlightDone={() => setHighlightAgencyName(false)}
              />
            )}
          </Box>
        </Box>

        {/* ── RIGHT COLUMN: Map ── */}
        <Box
          w={{ base: '100%', xl: '40%' }}
          flexShrink={0}
          position={{ base: 'relative', xl: 'sticky' }}
          top={{ xl: '24px' }}
          h={{ xl: 'calc(100vh - 48px)' }}
        >
          <ScrapeMapPanel scrapes={scrapes} />
        </Box>
      </Flex>

      {/* ════════════════════════════════════════
          CLIENT SCRAPE ASSIGNMENT MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={assignModal.isOpen} onClose={assignModal.onClose} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={2} px={6}>
            <HStack spacing={4}>
              <Avatar
                size="md"
                name={selectedClient?.name}
                bg="blue.500"
                color="white"
              />
              <Box>
                <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
                  {selectedClient?.name}
                </Text>
                {selectedClient?.businessName && (
                  <Text fontSize="sm" color={subtleText} fontWeight="400">
                    {selectedClient.businessName}
                  </Text>
                )}
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pt={4} pb={2}>
            <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em" mb={3}>
              Assigned Market Report
            </Text>
            <Text fontSize="sm" color={subtleText} mb={4}>
              Select which market report this client is assigned to.
            </Text>
            <VStack align="stretch" spacing={2}>
              {scrapes.map((scrape) => {
                const isSelected = pendingScrape === scrape.id;

                return (
                  <HStack
                    key={scrape.id}
                    p={3.5}
                    borderWidth="1px"
                    borderColor={isSelected ? 'blue.200' : subtleBorder}
                    borderRadius="12px"
                    bg={isSelected ? 'blue.50' : 'transparent'}
                    cursor="pointer"
                    _hover={{
                      bg: isSelected ? 'blue.50' : hoverBg,
                      borderColor: isSelected ? 'blue.300' : 'gray.300',
                    }}
                    transition="all 0.15s"
                    onClick={() => setPendingScrape(scrape.id)}
                    justify="space-between"
                  >
                    <HStack spacing={3}>
                      <Flex
                        w="36px"
                        h="36px"
                        borderRadius="10px"
                        bg={isSelected ? 'blue.100' : hoverBg}
                        justify="center"
                        align="center"
                        flexShrink={0}
                      >
                        <Icon as={LuDatabase} boxSize="15px" color={isSelected ? 'blue.500' : 'gray.400'} />
                      </Flex>
                      <Box>
                        <Text fontSize="sm" fontWeight="600" textTransform="capitalize" color={headingColor}>
                          {scrape.name || scrape.id}
                        </Text>
                        <HStack spacing={2} mt={0.5}>
                          {scrape.region && (
                            <HStack spacing={1}>
                              <Icon as={LuMapPin} boxSize="10px" color={subtleText} />
                              <Text fontSize="xs" color={subtleText}>{scrape.region}</Text>
                            </HStack>
                          )}
                          {scrape.businessCount != null && (
                            <HStack spacing={1}>
                              <Icon as={LuBuilding2} boxSize="10px" color={subtleText} />
                              <Text fontSize="xs" color={subtleText}>{scrape.businessCount} businesses</Text>
                            </HStack>
                          )}
                        </HStack>
                      </Box>
                    </HStack>
                    {isSelected && (
                      <Flex
                        w="24px"
                        h="24px"
                        borderRadius="full"
                        bg="blue.500"
                        justify="center"
                        align="center"
                        flexShrink={0}
                      >
                        <Icon as={LuCheck} boxSize="12px" color="white" strokeWidth={3} />
                      </Flex>
                    )}
                  </HStack>
                );
              })}
            </VStack>
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={4}>
            <HStack spacing={3} w="100%">
              <Button
                variant="ghost"
                onClick={assignModal.onClose}
                size="md"
                borderRadius="10px"
                fontWeight="500"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                size="md"
                onClick={handleSaveAssignment}
                isLoading={isSavingAssignment}
                isDisabled={!pendingScrape}
                borderRadius="10px"
                fontWeight="600"
                flex={2}
                leftIcon={<Icon as={LuCheck} />}
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                transition="all 0.15s"
              >
                Save Assignment
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          CREATE SCRAPE MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={scrapeModal.isOpen} onClose={handleCloseScrapeModal} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={1} px={6}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="12px"
                bg="blue.50"
                justify="center"
                align="center"
                flexShrink={0}
              >
                <Icon as={scrapeStep === 1 ? LuPlus : LuUsers} boxSize="18px" color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
                  {scrapeStep === 1 ? 'New Market Report' : 'Your Competitors'}
                </Text>
                <Text fontSize="sm" color={subtleText} fontWeight="400">
                  {scrapeStep === 1
                    ? 'Choose a business — we\'ll recommend the best market search'
                    : isDiscoveringCompetitors
                      ? 'Discovering competitors in this market...'
                      : `${discoveredCompetitors.length} competitors found — review before starting`}
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pt={5} pb={2}>
            {scrapeStep === 1 ? (
              <MarketSearchWizard
                onComplete={handleMarketSearchComplete}
                onCancel={handleCloseScrapeModal}
              />
            ) : (
              /* ── Step 2: Competitor Preview ── */
              <VStack spacing={4} align="stretch">
                {/* Scrape details summary */}
                <Box
                  p={4}
                  bg="blue.50"
                  borderRadius="12px"
                  borderWidth="1px"
                  borderColor="blue.200"
                >
                  <HStack spacing={2} mb={2}>
                    <Icon as={LuSearch} boxSize="13px" color="blue.500" />
                    <Text fontSize="10px" fontWeight="700" color="blue.600" textTransform="uppercase" letterSpacing="0.07em">
                      Market Search Used
                    </Text>
                  </HStack>
                  {anchorBusiness && (
                    <Text fontSize="xs" color="blue.700" fontWeight="600" mb={2}>
                      Anchor: {anchorBusiness.name}
                    </Text>
                  )}
                  <Text fontSize="sm" fontWeight="700" color="blue.900" mb={1.5}>
                    "{pendingVerticalId}"
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    {pendingScrapeNiche && (
                      <HStack spacing={1.5}>
                        <Icon as={LuHash} boxSize="11px" color="blue.500" />
                        <Text fontSize="xs" color="blue.700" fontWeight="500">{pendingScrapeNiche}</Text>
                      </HStack>
                    )}
                    {pendingScrapeRegion && (
                      <HStack spacing={1.5}>
                        <Icon as={LuMapPin} boxSize="11px" color="blue.500" />
                        <Text fontSize="xs" color="blue.700" fontWeight="500">{pendingScrapeRegion}</Text>
                      </HStack>
                    )}
                    {marketResolution && (
                      <>
                        <HStack spacing={1.5}>
                          <Icon as={LuSparkles} boxSize="11px" color="blue.500" />
                          <Text fontSize="xs" color="blue.700" fontWeight="500">
                            Match quality: {marketResolution.score}/100
                          </Text>
                        </HStack>
                        <HStack spacing={1.5}>
                          <Icon as={LuCheckCircle2} boxSize="11px" color="blue.500" />
                          <Text fontSize="xs" color="blue.700" fontWeight="500">
                            {marketResolution.targetFound
                              ? `Prospect found at #${marketResolution.targetPosition}`
                              : 'Prospect not resolved yet'}
                          </Text>
                        </HStack>
                      </>
                    )}
                  </HStack>
                </Box>

                {/* Competitor list */}
                {isDiscoveringCompetitors ? (
                  <Flex justify="center" align="center" py={10} direction="column" gap={3}>
                    <Spinner size="lg" color="blue.500" thickness="3px" speed="0.65s" />
                    <Text fontSize="sm" color={subtleText} fontWeight="500">
                      Discovering competitors in this market...
                    </Text>
                  </Flex>
                ) : discoveredCompetitors.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em">
                      {discoveredCompetitors.length} Competitors Found
                    </Text>
                    <Box maxH="340px" overflowY="auto" pr={1}
                      css={{
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-gray-300)', borderRadius: '2px' },
                      }}
                    >
                      <VStack spacing={2} align="stretch">
                        {discoveredCompetitors.map((comp) => (
                          <Box
                            key={comp.place_id || comp.name}
                            p={3}
                            bg={inputBg}
                            borderRadius="10px"
                            borderWidth="1px"
                            borderColor={subtleBorder}
                            _hover={{ borderColor: 'blue.200' }}
                            transition="border-color 0.15s"
                          >
                            <HStack justify="space-between" align="flex-start" spacing={3}>
                              <Box flex={1} minW={0}>
                                <HStack spacing={2} align="center">
                                  <Flex
                                    w="26px"
                                    h="26px"
                                    borderRadius="6px"
                                    bg="blue.50"
                                    justify="center"
                                    align="center"
                                    flexShrink={0}
                                  >
                                    <Icon as={LuBuilding2} boxSize="12px" color="blue.500" />
                                  </Flex>
                                  <Text fontSize="sm" fontWeight="600" color={headingColor} noOfLines={1}>
                                    {comp.name}
                                  </Text>
                                </HStack>
                                {comp.address && (
                                  <HStack spacing={1} mt={1} ml="34px">
                                    <Icon as={LuMapPin} boxSize="11px" color="gray.400" />
                                    <Text fontSize="xs" color={subtleText} noOfLines={1}>
                                      {comp.address}
                                    </Text>
                                  </HStack>
                                )}
                              </Box>
                              <VStack spacing={0.5} align="flex-end" flexShrink={0}>
                                {comp.rating != null && (
                                  <HStack spacing={1}>
                                    <Icon as={LuStar} boxSize="11px" color="orange.400" />
                                    <Text fontSize="xs" fontWeight="600" color={headingColor}>
                                      {comp.rating.toFixed(1)}
                                    </Text>
                                  </HStack>
                                )}
                                {comp.review_count > 0 && (
                                  <Text fontSize="10px" color={subtleText}>
                                    {comp.review_count.toLocaleString()} reviews
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                ) : (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py={8}
                    px={4}
                    bg={inputBg}
                    borderRadius="12px"
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    textAlign="center"
                    gap={2}
                  >
                    <Flex
                      w="48px"
                      h="48px"
                      borderRadius="12px"
                      bg="gray.100"
                      justify="center"
                      align="center"
                      mb={1}
                    >
                      <Icon as={LuSearch} boxSize={5} color="gray.400" />
                    </Flex>
                    <Text fontSize="sm" fontWeight="600" color={subtleText}>
                      No competitor preview available
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {"We'll discover competitors when the market report runs."}
                    </Text>
                  </Flex>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={scrapeStep === 2 ? 4 : 5}>
            {scrapeStep === 1 ? null : (
              <VStack spacing={2} w="100%">
                <Button
                  size="lg"
                  w="100%"
                  h="56px"
                  bg="blue.500"
                  color="white"
                  _hover={{
                    bg: 'blue.600',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(49, 130, 206, 0.45)',
                  }}
                  _active={{ transform: 'translateY(0px)', bg: 'blue.700' }}
                  isLoading={isTriggeringScrape}
                  isDisabled={isDiscoveringCompetitors}
                  onClick={handleConfirmScrape}
                  leftIcon={<Icon as={LuZap} boxSize="18px" />}
                  borderRadius="12px"
                  fontWeight="700"
                  fontSize="md"
                  letterSpacing="-0.01em"
                  boxShadow="0 4px 15px rgba(49, 130, 206, 0.35)"
                  transition="all 0.2s"
                >
                  Use Free Market Report
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setScrapeStep(1)}
                  w="100%"
                  borderRadius="10px"
                  fontWeight="500"
                  color={subtleText}
                  leftIcon={<Icon as={LuArrowLeft} boxSize="14px" />}
                  _hover={{ bg: hoverBg, color: headingColor }}
                >
                  Back to Settings
                </Button>
              </VStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          KEYWORD SCRAPE MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={keywordScrapeModal.isOpen} onClose={handleCloseKeywordScrapeModal} size="xl" isCentered scrollBehavior="inside">
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={1} px={6}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="12px"
                bg="purple.50"
                justify="center"
                align="center"
                flexShrink={0}
              >
                <Icon as={kwScrapeStep === 1 ? LuKeyboard : LuUsers} boxSize="18px" color="purple.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
                  {kwScrapeStep === 1 ? 'New Keyword Market Report' : 'Your Competitors'}
                </Text>
                <Text fontSize="sm" color={subtleText} fontWeight="400">
                  {kwScrapeStep === 1
                    ? 'Track keyword rankings for a market or business'
                    : isDiscoveringKwCompetitors
                      ? 'Discovering competitors in this market...'
                      : `${discoveredKwCompetitors.length} competitors found — review before starting`}
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pt={5} pb={2}>
            {kwScrapeStep === 1 ? (
              <>
                <VStack spacing={4} align="stretch">
                  <Box>
                    <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                      Search Keyword
                    </Text>
                    <InputGroup>
                      <InputLeftElement pointerEvents="none" h="44px">
                        <Icon as={LuSearch} color="gray.400" boxSize="16px" />
                      </InputLeftElement>
                      <Input
                        placeholder='e.g. "plumbers in Dallas TX"'
                        value={kwKeyword}
                        onChange={(e) => setKwKeyword(e.target.value)}
                        bg={inputBg}
                        borderColor={subtleBorder}
                        borderRadius="10px"
                        h="44px"
                        fontSize="sm"
                        _focus={{ borderColor: 'purple.400', bg: cardBg }}
                        _placeholder={{ color: 'gray.400' }}
                      />
                    </InputGroup>
                    <Text fontSize="xs" color={subtleText} mt={1.5}>
                      Enter the Google search term you'd use to find businesses in this market.
                    </Text>
                  </Box>

                  <Divider borderColor={subtleBorder} />

                  <SimpleGrid columns={2} spacing={4}>
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                        Niche / Category
                      </Text>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none" h="44px">
                          <Icon as={LuHash} color="gray.400" boxSize="16px" />
                        </InputLeftElement>
                        <Input
                          placeholder="e.g. Home Services"
                          value={kwNiche}
                          onChange={(e) => setKwNiche(e.target.value)}
                          bg={inputBg}
                          borderColor={subtleBorder}
                          borderRadius="10px"
                          h="44px"
                          fontSize="sm"
                          _focus={{ borderColor: 'purple.400', bg: cardBg }}
                          _placeholder={{ color: 'gray.400' }}
                        />
                      </InputGroup>
                    </Box>
                    <Box>
                      <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                        Region
                      </Text>
                      <InputGroup>
                        <InputLeftElement pointerEvents="none" h="44px">
                          <Icon as={LuMapPin} color="gray.400" boxSize="16px" />
                        </InputLeftElement>
                        <Input
                          placeholder="e.g. Dallas, TX"
                          value={kwRegion}
                          onChange={(e) => setKwRegion(e.target.value)}
                          bg={inputBg}
                          borderColor={subtleBorder}
                          borderRadius="10px"
                          h="44px"
                          fontSize="sm"
                          _focus={{ borderColor: 'purple.400', bg: cardBg }}
                          _placeholder={{ color: 'gray.400' }}
                        />
                      </InputGroup>
                    </Box>
                  </SimpleGrid>
                </VStack>
              </>
            ) : (
              /* ── Step 2: Competitor Preview (identical to review scrape) ── */
              <VStack spacing={4} align="stretch">
                {/* Scrape details summary */}
                <Box
                  p={4}
                  bg="purple.50"
                  borderRadius="12px"
                  borderWidth="1px"
                  borderColor="purple.200"
                >
                  <HStack spacing={2} mb={2}>
                    <Icon as={LuKeyboard} boxSize="13px" color="purple.500" />
                    <Text fontSize="10px" fontWeight="700" color="purple.600" textTransform="uppercase" letterSpacing="0.07em">
                      Keyword Market Report Target
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="700" color="purple.900" mb={1.5}>
                    "{pendingKwVerticalId}"
                  </Text>
                  <HStack spacing={4} flexWrap="wrap">
                    {pendingKwNiche && (
                      <HStack spacing={1.5}>
                        <Icon as={LuHash} boxSize="11px" color="purple.500" />
                        <Text fontSize="xs" color="purple.700" fontWeight="500">{pendingKwNiche}</Text>
                      </HStack>
                    )}
                    {pendingKwRegion && (
                      <HStack spacing={1.5}>
                        <Icon as={LuMapPin} boxSize="11px" color="purple.500" />
                        <Text fontSize="xs" color="purple.700" fontWeight="500">{pendingKwRegion}</Text>
                      </HStack>
                    )}
                  </HStack>
                </Box>

                {/* Competitor list — same cards as review scrape */}
                {isDiscoveringKwCompetitors ? (
                  <Flex justify="center" align="center" py={10} direction="column" gap={3}>
                    <Spinner size="lg" color="purple.500" thickness="3px" speed="0.65s" />
                    <Text fontSize="sm" color={subtleText} fontWeight="500">
                      Discovering competitors in this market...
                    </Text>
                  </Flex>
                ) : discoveredKwCompetitors.length > 0 ? (
                  <VStack spacing={2} align="stretch">
                    <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em">
                      {discoveredKwCompetitors.length} Competitors Found
                    </Text>
                    <Box maxH="340px" overflowY="auto" pr={1}
                      css={{
                        '&::-webkit-scrollbar': { width: '4px' },
                        '&::-webkit-scrollbar-track': { background: 'transparent' },
                        '&::-webkit-scrollbar-thumb': { background: 'var(--chakra-colors-gray-300)', borderRadius: '2px' },
                      }}
                    >
                      <VStack spacing={2} align="stretch">
                        {discoveredKwCompetitors.map((comp) => (
                          <Box
                            key={comp.place_id || comp.name}
                            p={3}
                            bg={inputBg}
                            borderRadius="10px"
                            borderWidth="1px"
                            borderColor={subtleBorder}
                            _hover={{ borderColor: 'purple.200' }}
                            transition="border-color 0.15s"
                          >
                            <HStack justify="space-between" align="flex-start" spacing={3}>
                              <Box flex={1} minW={0}>
                                <HStack spacing={2} align="center">
                                  <Flex
                                    w="26px"
                                    h="26px"
                                    borderRadius="6px"
                                    bg="purple.50"
                                    justify="center"
                                    align="center"
                                    flexShrink={0}
                                  >
                                    <Icon as={LuBuilding2} boxSize="12px" color="purple.500" />
                                  </Flex>
                                  <Text fontSize="sm" fontWeight="600" color={headingColor} noOfLines={1}>
                                    {comp.name}
                                  </Text>
                                </HStack>
                                {comp.address && (
                                  <HStack spacing={1} mt={1} ml="34px">
                                    <Icon as={LuMapPin} boxSize="11px" color="gray.400" />
                                    <Text fontSize="xs" color={subtleText} noOfLines={1}>
                                      {comp.address}
                                    </Text>
                                  </HStack>
                                )}
                              </Box>
                              <VStack spacing={0.5} align="flex-end" flexShrink={0}>
                                {comp.rating != null && (
                                  <HStack spacing={1}>
                                    <Icon as={LuStar} boxSize="11px" color="orange.400" />
                                    <Text fontSize="xs" fontWeight="600" color={headingColor}>
                                      {comp.rating.toFixed(1)}
                                    </Text>
                                  </HStack>
                                )}
                                {comp.review_count > 0 && (
                                  <Text fontSize="10px" color={subtleText}>
                                    {comp.review_count.toLocaleString()} reviews
                                  </Text>
                                )}
                              </VStack>
                            </HStack>
                          </Box>
                        ))}
                      </VStack>
                    </Box>
                  </VStack>
                ) : (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py={8}
                    px={4}
                    bg={inputBg}
                    borderRadius="12px"
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    textAlign="center"
                    gap={2}
                  >
                    <Flex
                      w="48px"
                      h="48px"
                      borderRadius="12px"
                      bg="gray.100"
                      justify="center"
                      align="center"
                      mb={1}
                    >
                      <Icon as={LuSearch} boxSize={5} color="gray.400" />
                    </Flex>
                    <Text fontSize="sm" fontWeight="600" color={subtleText}>
                      No competitor preview available
                    </Text>
                    <Text fontSize="xs" color={subtleText}>
                      {"We'll discover competitors when the market report runs."}
                    </Text>
                  </Flex>
                )}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={kwScrapeStep === 2 ? 4 : 5}>
            {kwScrapeStep === 1 ? (
              <HStack spacing={3} w="100%">
                <Button
                  variant="ghost"
                  onClick={handleCloseKeywordScrapeModal}
                  size="md"
                  borderRadius="10px"
                  fontWeight="500"
                  flex={1}
                >
                  Cancel
                </Button>
                <Button
                  colorScheme="purple"
                  size="md"
                  onClick={handlePreviewKwCompetitors}
                  isLoading={isAnalyzingKwUrl}
                  isDisabled={!kwKeyword.trim()}
                  borderRadius="10px"
                  fontWeight="600"
                  flex={2}
                  leftIcon={<Icon as={LuUsers} boxSize="15px" />}
                  _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                  transition="all 0.15s"
                >
                  Preview Competitors
                </Button>
              </HStack>
            ) : (
              <VStack spacing={2} w="100%">
                <Button
                  size="lg"
                  w="100%"
                  h="56px"
                  bg="purple.500"
                  color="white"
                  _hover={{
                    bg: 'purple.600',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 30px rgba(128, 90, 213, 0.45)',
                  }}
                  _active={{ transform: 'translateY(0px)', bg: 'purple.700' }}
                  isLoading={isTriggeringKwScrape}
                  isDisabled={isDiscoveringKwCompetitors}
                  onClick={handleConfirmKeywordScrape}
                  leftIcon={<Icon as={LuZap} boxSize="18px" />}
                  borderRadius="12px"
                  fontWeight="700"
                  fontSize="md"
                  letterSpacing="-0.01em"
                  boxShadow="0 4px 15px rgba(128, 90, 213, 0.35)"
                  transition="all 0.2s"
                >
                  Start Keyword Market Report
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setKwScrapeStep(1)}
                  w="100%"
                  borderRadius="10px"
                  fontWeight="500"
                  color={subtleText}
                  leftIcon={<Icon as={LuArrowLeft} boxSize="14px" />}
                  _hover={{ bg: hoverBg, color: headingColor }}
                >
                  Back to Settings
                </Button>
              </VStack>
            )}
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          ADD CLIENT MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={addClientModal.isOpen} onClose={addClientModal.onClose} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={1} px={6}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="12px"
                bg="blue.50"
                justify="center"
                align="center"
                flexShrink={0}
              >
                <Icon as={LuUserPlus} boxSize="18px" color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
                  Add Client
                </Text>
                <Text fontSize="sm" color={subtleText} fontWeight="400">
                  Add a new client to your agency
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pt={5} pb={2}>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                    Client Name
                  </Text>
                  <Input
                    placeholder="Jane Smith"
                    value={addClientName}
                    onChange={(e) => setAddClientName(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                    Business Name
                  </Text>
                  <InputGroup>
                    <InputLeftElement pointerEvents="none" h="44px">
                      <Icon as={LuBuilding2} color="gray.400" boxSize="16px" />
                    </InputLeftElement>
                    <Input
                      placeholder="Smith Plumbing Co."
                      value={addClientBusiness}
                      onChange={(e) => setAddClientBusiness(e.target.value)}
                      bg={inputBg}
                      borderColor={subtleBorder}
                      borderRadius="10px"
                      h="44px"
                      fontSize="sm"
                      _focus={{ borderColor: 'blue.400', bg: cardBg }}
                      _placeholder={{ color: 'gray.400' }}
                    />
                  </InputGroup>
                </Box>
              </SimpleGrid>

              <Box>
                <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                  Email Address
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="44px">
                    <Icon as={LuMail} color="gray.400" boxSize="16px" />
                  </InputLeftElement>
                  <Input
                    placeholder="jane@smithplumbing.com"
                    type="email"
                    value={addClientEmail}
                    onChange={(e) => setAddClientEmail(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </InputGroup>
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                  Password
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="44px">
                    <Icon as={LuLock} color="gray.400" boxSize="16px" />
                  </InputLeftElement>
                  <Input
                    placeholder="Create a password for the client"
                    type={showClientPassword ? 'text' : 'password'}
                    value={addClientPassword}
                    onChange={(e) => setAddClientPassword(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                  <InputRightElement h="44px">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={showClientPassword ? 'Hide password' : 'Show password'}
                      icon={<Icon as={showClientPassword ? LuEyeOff : LuEye} boxSize="16px" color="gray.400" />}
                      onClick={() => setShowClientPassword(!showClientPassword)}
                      _hover={{ bg: 'transparent' }}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>

              <Divider borderColor={subtleBorder} />

              <Box>
                <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                  Assign to Market Report
                </Text>
                <Text fontSize="xs" color={subtleText} mb={3}>
                  Only agency admins can change the market report assignment.
                </Text>
                <VStack align="stretch" spacing={2}>
                  {scrapes.map((scrape) => {
                    const isSelected = addClientScrape === scrape.id;
                    return (
                      <HStack
                        key={scrape.id}
                        p={2.5}
                        borderWidth="1px"
                        borderColor={isSelected ? 'blue.200' : subtleBorder}
                        borderRadius="10px"
                        bg={isSelected ? 'blue.50' : 'transparent'}
                        cursor="pointer"
                        _hover={{ borderColor: isSelected ? 'blue.300' : 'gray.300' }}
                        transition="all 0.15s"
                        onClick={() => setAddClientScrape(scrape.id)}
                        justify="space-between"
                      >
                        <HStack spacing={3}>
                          <Flex
                            w="32px"
                            h="32px"
                            borderRadius="8px"
                            bg={isSelected ? 'blue.100' : hoverBg}
                            justify="center"
                            align="center"
                            flexShrink={0}
                          >
                            <Icon as={LuDatabase} boxSize="13px" color={isSelected ? 'blue.500' : 'gray.400'} />
                          </Flex>
                          <Box>
                            <Text fontSize="sm" fontWeight="500" textTransform="capitalize" color={headingColor}>
                              {scrape.name || scrape.id}
                            </Text>
                            {scrape.region && (
                              <Text fontSize="xs" color={subtleText}>{scrape.region}</Text>
                            )}
                          </Box>
                        </HStack>
                        {isSelected && (
                          <Flex
                            w="20px"
                            h="20px"
                            borderRadius="full"
                            bg="blue.500"
                            justify="center"
                            align="center"
                            flexShrink={0}
                          >
                            <Icon as={LuCheck} boxSize="10px" color="white" strokeWidth={3} />
                          </Flex>
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={5}>
            <HStack spacing={3} w="100%">
              <Button
                variant="ghost"
                onClick={addClientModal.onClose}
                size="md"
                borderRadius="10px"
                fontWeight="500"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                size="md"
                onClick={handleAddClient}
                isLoading={isAddingClient}
                isDisabled={!addClientEmail.trim() || !addClientPassword.trim()}
                borderRadius="10px"
                fontWeight="600"
                flex={2}
                leftIcon={<Icon as={LuPlus} />}
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                transition="all 0.15s"
              >
                Add Client
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          SCRAPE DETAILS MODAL
         ════════════════════════════════════════ */}
      <ScrapeDetailsModal
        isOpen={scrapeDetailsModal.isOpen}
        onClose={scrapeDetailsModal.onClose}
        scrape={selectedScrapeForDetails}
        details={scrapeDetails}
        isLoading={isLoadingScrapeDetails}
        scrapeStatuses={scrapeStatuses}
        clients={clients}
        cardBg={cardBg}
        subtleBorder={subtleBorder}
        subtleText={subtleText}
        headingColor={headingColor}
        hoverBg={hoverBg}
      />

      {/* ════════════════════════════════════════
          ADD MEMBER MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={addMemberModal.isOpen} onClose={addMemberModal.onClose} size="lg" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={1} px={6}>
            <HStack spacing={3}>
              <Flex
                w="40px"
                h="40px"
                borderRadius="12px"
                bg="blue.50"
                justify="center"
                align="center"
                flexShrink={0}
              >
                <Icon as={LuUserPlus} boxSize="18px" color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
                  Add Member
                </Text>
                <Text fontSize="sm" color={subtleText} fontWeight="400">
                  Create a new team member for your agency
                </Text>
              </Box>
            </HStack>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pt={5} pb={2}>
            <VStack spacing={4} align="stretch">
              <SimpleGrid columns={2} spacing={4}>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                    First Name
                  </Text>
                  <Input
                    placeholder="Jane"
                    value={addMemberFirstName}
                    onChange={(e) => setAddMemberFirstName(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </Box>
                <Box>
                  <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                    Last Name
                  </Text>
                  <Input
                    placeholder="Smith"
                    value={addMemberLastName}
                    onChange={(e) => setAddMemberLastName(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </Box>
              </SimpleGrid>

              <Box>
                <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                  Email Address
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="44px">
                    <Icon as={LuMail} color="gray.400" boxSize="16px" />
                  </InputLeftElement>
                  <Input
                    placeholder="jane@agency.com"
                    type="email"
                    value={addMemberEmail}
                    onChange={(e) => setAddMemberEmail(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                </InputGroup>
              </Box>

              <Box>
                <Text fontSize="xs" fontWeight="600" color={headingColor} mb={1.5}>
                  Password
                </Text>
                <InputGroup>
                  <InputLeftElement pointerEvents="none" h="44px">
                    <Icon as={LuLock} color="gray.400" boxSize="16px" />
                  </InputLeftElement>
                  <Input
                    placeholder="Create a password"
                    type={showMemberPassword ? 'text' : 'password'}
                    value={addMemberPassword}
                    onChange={(e) => setAddMemberPassword(e.target.value)}
                    bg={inputBg}
                    borderColor={subtleBorder}
                    borderRadius="10px"
                    h="44px"
                    fontSize="sm"
                    _focus={{ borderColor: 'blue.400', bg: cardBg }}
                    _placeholder={{ color: 'gray.400' }}
                  />
                  <InputRightElement h="44px">
                    <IconButton
                      variant="ghost"
                      size="sm"
                      aria-label={showMemberPassword ? 'Hide password' : 'Show password'}
                      icon={<Icon as={showMemberPassword ? LuEyeOff : LuEye} boxSize="16px" color="gray.400" />}
                      onClick={() => setShowMemberPassword(!showMemberPassword)}
                      _hover={{ bg: 'transparent' }}
                    />
                  </InputRightElement>
                </InputGroup>
              </Box>

              <Divider borderColor={subtleBorder} />

              <Box>
                <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em" mb={2}>
                  Assign to Vertical
                </Text>
                <Text fontSize="xs" color={subtleText} mb={3}>
                  Select which vertical this member will have access to.
                </Text>
                <VStack align="stretch" spacing={2}>
                  {scrapes.map((scrape) => {
                    const isSelected = addMemberVertical === scrape.id;
                    return (
                      <HStack
                        key={scrape.id}
                        p={2.5}
                        borderWidth="1px"
                        borderColor={isSelected ? 'blue.200' : subtleBorder}
                        borderRadius="10px"
                        bg={isSelected ? 'blue.50' : 'transparent'}
                        cursor="pointer"
                        _hover={{ borderColor: isSelected ? 'blue.300' : 'gray.300' }}
                        transition="all 0.15s"
                        onClick={() => setAddMemberVertical(scrape.id)}
                        justify="space-between"
                      >
                        <HStack spacing={3}>
                          <Flex
                            w="32px"
                            h="32px"
                            borderRadius="8px"
                            bg={isSelected ? 'blue.100' : hoverBg}
                            justify="center"
                            align="center"
                            flexShrink={0}
                          >
                            <Icon as={LuDatabase} boxSize="13px" color={isSelected ? 'blue.500' : 'gray.400'} />
                          </Flex>
                          <Box>
                            <Text fontSize="sm" fontWeight="500" textTransform="capitalize" color={headingColor}>
                              {scrape.name || scrape.id}
                            </Text>
                            {scrape.region && (
                              <Text fontSize="xs" color={subtleText}>{scrape.region}</Text>
                            )}
                          </Box>
                        </HStack>
                        {isSelected && (
                          <Flex
                            w="20px"
                            h="20px"
                            borderRadius="full"
                            bg="blue.500"
                            justify="center"
                            align="center"
                            flexShrink={0}
                          >
                            <Icon as={LuCheck} boxSize="10px" color="white" strokeWidth={3} />
                          </Flex>
                        )}
                      </HStack>
                    );
                  })}
                </VStack>
              </Box>
            </VStack>
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={5}>
            <HStack spacing={3} w="100%">
              <Button
                variant="ghost"
                onClick={addMemberModal.onClose}
                size="md"
                borderRadius="10px"
                fontWeight="500"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                size="md"
                onClick={handleAddMember}
                isLoading={isAddingMember}
                isDisabled={!addMemberEmail.trim() || !addMemberPassword.trim() || !addMemberVertical}
                borderRadius="10px"
                fontWeight="600"
                flex={2}
                leftIcon={<Icon as={LuPlus} />}
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                transition="all 0.15s"
              >
                Add Member
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          DELETE MEMBER CONFIRMATION MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={deleteMemberModal.isOpen} onClose={deleteMemberModal.onClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(6px)" />
        <ModalContent bg={cardBg} borderRadius="16px" shadow="2xl" mx={4}>
          <ModalHeader pt={6} pb={2} px={6}>
            <Text fontSize="lg" fontWeight="700" color={headingColor} letterSpacing="-0.01em">
              Remove Member
            </Text>
          </ModalHeader>
          <ModalCloseButton top={6} right={6} />
          <ModalBody px={6} pb={2}>
            <Text fontSize="sm" color={subtleText}>
              Are you sure you want to remove <strong>{memberToDelete?.email}</strong> from your agency?
              This action cannot be undone.
            </Text>
          </ModalBody>
          <ModalFooter px={6} pb={6} pt={4}>
            <HStack spacing={3} w="100%">
              <Button
                variant="ghost"
                onClick={deleteMemberModal.onClose}
                size="md"
                borderRadius="10px"
                fontWeight="500"
                flex={1}
              >
                Cancel
              </Button>
              <Button
                colorScheme="red"
                size="md"
                onClick={handleDeleteMember}
                isLoading={isDeletingMember}
                borderRadius="10px"
                fontWeight="600"
                flex={2}
                leftIcon={<Icon as={LuTrash2} />}
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                transition="all 0.15s"
              >
                Remove
              </Button>
            </HStack>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* ════════════════════════════════════════
          UPGRADE / CHECKOUT MODAL
         ════════════════════════════════════════ */}
      <Modal isOpen={checkoutModal.isOpen} onClose={checkoutModal.onClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.500" backdropFilter="blur(8px)" />
        <ModalContent bg={cardBg} borderRadius="20px" shadow="2xl" mx={4} overflow="hidden">
          {/* Gradient header */}
          <Box
            bg="linear-gradient(135deg, #1a365d 0%, #2b6cb0 50%, #3182ce 100%)"
            px={6}
            pt={8}
            pb={8}
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="-30px"
              right="-20px"
              w="120px"
              h="120px"
              borderRadius="full"
              bg="whiteAlpha.100"
            />
            <Box
              position="absolute"
              bottom="-40px"
              left="10px"
              w="80px"
              h="80px"
              borderRadius="full"
              bg="whiteAlpha.50"
            />
            <ModalCloseButton color="white" top={4} right={4} />
            <VStack spacing={3} align="start" position="relative">
              <Flex
                w="48px"
                h="48px"
                borderRadius="14px"
                bg="whiteAlpha.200"
                justify="center"
                align="center"
              >
                <Icon as={LuRocket} boxSize="22px" color="white" />
              </Flex>
              <Box>
                <Text fontSize="xl" fontWeight="800" color="white" letterSpacing="-0.02em">
                  Upgrade to Pro
                </Text>
                <Text fontSize="sm" color="whiteAlpha.800" fontWeight="400" mt={0.5}>
                  You've used all 3 free market reports. Unlock unlimited market intelligence.
                </Text>
              </Box>
              <Badge
                bg="whiteAlpha.200"
                color="white"
                fontSize="xs"
                px={2.5}
                py={1}
                borderRadius="full"
                fontWeight="600"
              >
                3 / 3 Free Market Reports Used
              </Badge>
            </VStack>
          </Box>

          <ModalBody px={6} pt={6} pb={2}>
            {/* Pricing card */}
            <Box
              p={4}
              borderWidth="2px"
              borderColor="blue.400"
              borderRadius="14px"
              bg={checkoutPricingBg}
              mb={5}
              position="relative"
              overflow="hidden"
            >
              <Badge
                position="absolute"
                top={3}
                right={3}
                bg="blue.500"
                color="white"
                fontSize="10px"
                px={2}
                py={0.5}
                borderRadius="full"
                fontWeight="700"
              >
                MOST POPULAR
              </Badge>
              <HStack spacing={2} mb={1}>
                <Icon as={LuZap} boxSize="16px" color="blue.500" />
                <Text fontSize="xs" fontWeight="700" color="blue.600" textTransform="uppercase" letterSpacing="0.08em">
                  Pro Plan
                </Text>
              </HStack>
              <HStack align="baseline" spacing={1} mb={1}>
                <Text fontSize="3xl" fontWeight="900" color={checkoutPriceColor} letterSpacing="-0.03em">
                  $497
                </Text>
                <Text fontSize="sm" color="blue.500" fontWeight="500">
                  /month
                </Text>
              </HStack>
              <Text fontSize="xs" color="blue.500" fontWeight="500">
                $497/mo
              </Text>
            </Box>

            {/* Feature list */}
            <VStack align="stretch" spacing={2.5} mb={5}>
              {[
                '20 active market reports',
                'Real-time competitive intelligence',
                'Up to 20 competitors per market report',
                'AI-powered keyword extraction',
                'Multi-region market analysis',
                'Priority support & SLA',
              ].map((text) => (
                <HStack key={text} spacing={3}>
                  <Flex
                    w="22px"
                    h="22px"
                    borderRadius="full"
                    bg={checkoutFeatureCheckBg}
                    justify="center"
                    align="center"
                    flexShrink={0}
                  >
                    <Icon as={LuCheck} boxSize="11px" color="green.600" strokeWidth={3} />
                  </Flex>
                  <Text fontSize="sm" color={headingColor} fontWeight="500">
                    {text}
                  </Text>
                </HStack>
              ))}
            </VStack>
          </ModalBody>

          <ModalFooter px={6} pb={6} pt={2}>
            <VStack spacing={3} w="100%">
              <Button
                w="100%"
                size="lg"
                bg="linear-gradient(135deg, #2b6cb0 0%, #3182ce 100%)"
                color="white"
                borderRadius="12px"
                fontWeight="700"
                fontSize="15px"
                leftIcon={<Icon as={LuCreditCard} boxSize="16px" />}
                rightIcon={<Icon as={LuArrowRight} boxSize="14px" />}
                _hover={{
                  bg: 'linear-gradient(135deg, #2c5282 0%, #2b6cb0 100%)',
                  transform: 'translateY(-1px)',
                  shadow: 'lg',
                }}
                transition="all 0.2s"
                onClick={() => {
                  trackLead({
                    content_name: 'Agency Pro Plan',
                    content_category: 'agency_white_label',
                    currency: 'USD',
                    value: 497,
                  });
                  checkoutModal.onClose();
                  toast({
                    title: 'Upgrade request received',
                    description: 'Our team will be in touch shortly to set up your Pro plan.',
                    status: 'info',
                    duration: 5000,
                    isClosable: true,
                    position: 'bottom-right',
                  });
                }}
              >
                Get Started — $497/mo
              </Button>
              <Button
                w="100%"
                variant="ghost"
                size="sm"
                borderRadius="10px"
                color={subtleText}
                fontWeight="500"
                onClick={checkoutModal.onClose}
              >
                Maybe Later
              </Button>
            </VStack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

/* ═══════════════════════════════════════════
   SCRAPE DETAILS MODAL
   ═══════════════════════════════════════════ */
function ScrapeDetailsModal({
  isOpen,
  onClose,
  scrape,
  details,
  isLoading,
  scrapeStatuses,
  clients,
  cardBg,
  subtleBorder,
  subtleText,
  headingColor,
  hoverBg,
}) {
  const sectionLabelColor = useColorModeValue('gray.500', 'gray.400');
  const tableBg = useColorModeValue('gray.50', 'gray.800');
  const tableHover = useColorModeValue('blue.50', 'blue.900');
  const statBg = useColorModeValue('white', 'gray.800');
  const statBorder = useColorModeValue('gray.100', 'gray.700');
  const rankBg = useColorModeValue('gray.100', 'gray.700');
  const rankColor = useColorModeValue('gray.400', 'gray.500');
  const tableRowBorder = useColorModeValue('gray.100', 'gray.700');

  if (!scrape) return null;

  const latestStatus = details?.latest_status || null;
  const businesses = details?.businesses || [];
  const assignedClients = details?.assigned_clients || [];

  const STATUS_CONFIG = {
    RUNNING:   { dotColor: '#F6AD55', pillBg: 'rgba(246,173,85,0.18)',  label: 'Running'   },
    SUCCEEDED: { dotColor: '#48BB78', pillBg: 'rgba(72,187,120,0.18)',  label: 'Succeeded' },
    FAILED:    { dotColor: '#FC8181', pillBg: 'rgba(252,129,129,0.18)', label: 'Failed'    },
  };
  const sc = STATUS_CONFIG[latestStatus?.status] || { dotColor: '#A0AEC0', pillBg: 'rgba(160,174,192,0.18)', label: latestStatus?.status || '' };

  const formatTime = (isoStr) => {
    if (!isoStr) return 'N/A';
    try {
      const d = new Date(isoStr);
      return d.toLocaleString(undefined, {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    } catch {
      return isoStr;
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="5xl" isCentered scrollBehavior="inside">
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(10px)" />
      <ModalContent
        bg={cardBg}
        borderRadius="20px"
        shadow="0 30px 80px rgba(0,0,0,0.25)"
        mx={4}
        maxH="90vh"
        overflow="hidden"
        borderWidth="1px"
        borderColor={subtleBorder}
      >
        {/* ── Gradient hero header ── */}
        <Box
          position="relative"
          bgGradient="linear(135deg, blue.600 0%, purple.600 50%, purple.700 100%)"
          px={{ base: 6, md: 10 }}
          pt={8}
          pb={9}
          overflow="hidden"
        >
          {/* Decorative background shapes */}
          <Box position="absolute" top="-60px" right="-40px" w="220px" h="220px" borderRadius="full" bg="whiteAlpha.100" pointerEvents="none" />
          <Box position="absolute" bottom="-70px" right="140px" w="150px" h="150px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />
          <Box position="absolute" top="30px" left="60%" w="80px" h="80px" borderRadius="full" bg="whiteAlpha.50" pointerEvents="none" />
          <Box position="absolute" bottom="10px" left="-20px" w="100px" h="100px" borderRadius="full" bg="whiteAlpha.30" pointerEvents="none" />

          <IconButton
            icon={<Icon as={LuX} boxSize="16px" />}
            onClick={onClose}
            variant="ghost"
            color="whiteAlpha.700"
            _hover={{ bg: 'whiteAlpha.200', color: 'white' }}
            borderRadius="full"
            size="sm"
            aria-label="Close"
            position="absolute"
            top={4}
            right={4}
            zIndex={2}
          />

          <VStack spacing={5} align="flex-start">
            <Flex
              w="48px"
              h="48px"
              borderRadius="14px"
              bg="whiteAlpha.200"
              justify="center"
              align="center"
              border="1px solid"
              borderColor="whiteAlpha.300"
              backdropFilter="blur(10px)"
            >
              <Icon as={LuDatabase} boxSize="20px" color="white" />
            </Flex>

            <Box>
              <Text
                fontSize={{ base: "xl", md: "2xl" }}
                fontWeight="700"
                color="white"
                letterSpacing="-0.02em"
                lineHeight="1.3"
                mb={3}
                wordBreak="break-word"
              >
                {(scrape.name || scrape.id || '').replace(/-/g, ' ')}
              </Text>

              <HStack spacing={3} flexWrap="wrap" rowGap={2}>
                {scrape.region && (
                  <HStack
                    spacing={1.5}
                    bg="whiteAlpha.150"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                  >
                    <Icon as={LuMapPin} boxSize="12px" color="whiteAlpha.800" />
                    <Text fontSize="xs" color="whiteAlpha.900" fontWeight="600">{scrape.region}</Text>
                  </HStack>
                )}
                {scrape.niche && (
                  <HStack
                    spacing={1.5}
                    bg="whiteAlpha.150"
                    px={3}
                    py={1.5}
                    borderRadius="full"
                  >
                    <Text fontSize="xs" color="whiteAlpha.900" fontWeight="600">{scrape.niche}</Text>
                  </HStack>
                )}
                {latestStatus && (
                  <HStack
                    spacing={2}
                    px={3}
                    py={1.5}
                    borderRadius="full"
                    bg={sc.pillBg}
                    backdropFilter="blur(8px)"
                    border="1px solid"
                    borderColor="whiteAlpha.300"
                  >
                    <Box
                      w="7px"
                      h="7px"
                      borderRadius="full"
                      bg={sc.dotColor}
                      boxShadow={`0 0 8px ${sc.dotColor}`}
                    />
                    <Text fontSize="10px" fontWeight="700" color="white" textTransform="uppercase" letterSpacing="0.08em">
                      {sc.label}
                    </Text>
                  </HStack>
                )}
              </HStack>
            </Box>
          </VStack>
        </Box>

        <ModalBody px={8} py={7}>
          {isLoading ? (
            <VStack spacing={5} align="stretch">
              <SimpleGrid columns={3} spacing={4}>
                {[1, 2, 3].map(i => (
                  <Skeleton key={i} h="100px" borderRadius="16px" />
                ))}
              </SimpleGrid>
              <Skeleton h="18px" w="150px" borderRadius="6px" mt={2} />
              {[1, 2, 3, 4, 5].map(i => (
                <Skeleton key={i} h="52px" borderRadius="12px" />
              ))}
            </VStack>
          ) : (
            <VStack spacing={8} align="stretch">

              {/* ── Stats row ── */}
              <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={4}>

                {/* Started */}
                <Box
                  p={5}
                  borderRadius="16px"
                  bg={statBg}
                  borderWidth="1px"
                  borderColor={statBorder}
                  position="relative"
                  overflow="hidden"
                  shadow="sm"
                >
                  <Box
                    position="absolute"
                    top={0} left={0} right={0}
                    h="3px"
                    bgGradient="linear(90deg, blue.400, blue.600)"
                  />
                  <HStack spacing={2.5} mb={3}>
                    <Flex w="30px" h="30px" borderRadius="9px" bg="blue.50" justify="center" align="center">
                      <Icon as={LuClock} boxSize="13px" color="blue.500" />
                    </Flex>
                    <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">
                      Market Report Started
                    </Text>
                  </HStack>
                  <Text fontSize="sm" fontWeight="700" color={headingColor} lineHeight="1.4">
                    {formatTime(latestStatus?.start_time || scrape.created_at)}
                  </Text>
                </Box>

                {/* Businesses Found */}
                <Box
                  p={5}
                  borderRadius="16px"
                  bg={statBg}
                  borderWidth="1px"
                  borderColor={statBorder}
                  position="relative"
                  overflow="hidden"
                  shadow="sm"
                >
                  <Box
                    position="absolute"
                    top={0} left={0} right={0}
                    h="3px"
                    bgGradient="linear(90deg, purple.400, purple.600)"
                  />
                  <HStack spacing={2.5} mb={3}>
                    <Flex w="30px" h="30px" borderRadius="9px" bg="purple.50" justify="center" align="center">
                      <Icon as={LuBuilding2} boxSize="13px" color="purple.500" />
                    </Flex>
                    <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">
                      Businesses Found
                    </Text>
                  </HStack>
                  <Text fontSize="4xl" fontWeight="800" color={headingColor} lineHeight="1">
                    {businesses.length}
                  </Text>
                </Box>

                {/* Assigned Clients */}
                <Box
                  p={5}
                  borderRadius="16px"
                  bg={statBg}
                  borderWidth="1px"
                  borderColor={statBorder}
                  position="relative"
                  overflow="hidden"
                  shadow="sm"
                >
                  <Box
                    position="absolute"
                    top={0} left={0} right={0}
                    h="3px"
                    bgGradient="linear(90deg, teal.400, teal.600)"
                  />
                  <HStack spacing={2.5} mb={3}>
                    <Flex w="30px" h="30px" borderRadius="9px" bg="teal.50" justify="center" align="center">
                      <Icon as={LuUsers} boxSize="13px" color="teal.500" />
                    </Flex>
                    <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">
                      Assigned Clients
                    </Text>
                  </HStack>
                  <Text fontSize="4xl" fontWeight="800" color={headingColor} lineHeight="1">
                    {assignedClients.length}
                  </Text>
                </Box>
              </SimpleGrid>

              {/* ── Running Countdown Bar ── */}
              {latestStatus?.status === 'RUNNING' && (
                <ScrapeProgressFeed
                  startTime={latestStatus?.start_time}
                  verticalId={scrape.id}
                  competitorCount={businesses.length || scrape.businessCount}
                />
              )}

              {/* ── Assigned Clients List (only when clients exist) ── */}
              {assignedClients.length > 0 && (
                <Box>
                  <Text
                    fontSize="9px"
                    fontWeight="800"
                    color={sectionLabelColor}
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                    mb={3}
                  >
                    Clients
                  </Text>
                  <Wrap spacing={2}>
                    {assignedClients.map((c, idx) => (
                      <WrapItem key={idx}>
                        <HStack
                          p={3}
                          borderRadius="12px"
                          borderWidth="1px"
                          borderColor={subtleBorder}
                          spacing={2.5}
                          _hover={{ bg: hoverBg }}
                          transition="background 0.15s"
                        >
                          <Avatar size="sm" name={c.name || c.email} bg="blue.500" color="white" />
                          <Box>
                            <Text fontSize="sm" fontWeight="600" color={headingColor}>
                              {c.name || 'Unnamed Client'}
                            </Text>
                            {c.business_name && (
                              <Text fontSize="xs" color={subtleText}>{c.business_name}</Text>
                            )}
                            <Text fontSize="xs" color={subtleText}>{c.email}</Text>
                          </Box>
                        </HStack>
                      </WrapItem>
                    ))}
                  </Wrap>
                </Box>
              )}

              {/* ── Competitors Table ── */}
              <Box>
                <HStack spacing={2.5} mb={4} align="center">
                  <Flex w="30px" h="30px" borderRadius="9px" bg="orange.50" justify="center" align="center">
                    <Icon as={LuBuilding2} boxSize="13px" color="orange.500" />
                  </Flex>
                  <Text
                    fontSize="9px"
                    fontWeight="800"
                    color={sectionLabelColor}
                    textTransform="uppercase"
                    letterSpacing="0.1em"
                  >
                    Competitors
                  </Text>
                  <Badge
                    bg="orange.50"
                    color="orange.600"
                    fontSize="10px"
                    borderRadius="full"
                    px={2.5}
                    py={0.5}
                    fontWeight="800"
                  >
                    {businesses.length}
                  </Badge>
                </HStack>

                {businesses.length > 0 ? (
                  <Box
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    borderRadius="16px"
                    overflow="hidden"
                    shadow="sm"
                  >
                    <Table size="sm" variant="unstyled">
                      <Thead>
                        <Tr bg={tableBg}>
                          <Th w="54px" py={3.5} px={5}>
                            <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">#</Text>
                          </Th>
                          <Th py={3.5} px={3}>
                            <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">Business</Text>
                          </Th>
                          <Th py={3.5} px={5} isNumeric>
                            <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">Rating</Text>
                          </Th>
                          <Th py={3.5} px={5} isNumeric>
                            <Text fontSize="9px" fontWeight="800" color={sectionLabelColor} textTransform="uppercase" letterSpacing="0.1em">Reviews</Text>
                          </Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {businesses.map((biz, idx) => (
                          <Tr
                            key={idx}
                            _hover={{ bg: tableHover }}
                            transition="background 0.15s"
                            borderTopWidth="1px"
                            borderTopColor={tableRowBorder}
                          >
                            <Td py={3.5} px={5}>
                              <Flex
                                w="26px"
                                h="26px"
                                borderRadius="8px"
                                bg={rankBg}
                                justify="center"
                                align="center"
                              >
                                <Text fontSize="11px" fontWeight="700" color={rankColor}>{idx + 1}</Text>
                              </Flex>
                            </Td>
                            <Td py={3.5} px={3}>
                              <Text fontSize="sm" fontWeight="600" color={headingColor} noOfLines={1}>
                                {biz.name}
                              </Text>
                              {biz.address && (
                                <Text fontSize="xs" color={subtleText} noOfLines={1} mt={0.5}>
                                  {biz.address}
                                </Text>
                              )}
                            </Td>
                            <Td py={3.5} px={5} isNumeric>
                              {biz.rating_avg != null ? (
                                <HStack spacing={1.5} justify="flex-end">
                                  <Icon as={LuStar} boxSize="12px" color="yellow.400" />
                                  <Text fontSize="sm" fontWeight="700" color={headingColor}>
                                    {Number(biz.rating_avg).toFixed(1)}
                                  </Text>
                                </HStack>
                              ) : (
                                <Text fontSize="sm" color={subtleText}>—</Text>
                              )}
                            </Td>
                            <Td py={3.5} px={5} isNumeric>
                              <Text fontSize="sm" fontWeight="600" color={headingColor}>
                                {biz.rating_count != null ? biz.rating_count.toLocaleString() : '—'}
                              </Text>
                            </Td>
                          </Tr>
                        ))}
                      </Tbody>
                    </Table>
                  </Box>
                ) : (
                  <VStack
                    p={10}
                    spacing={3}
                    align="center"
                    borderRadius="16px"
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    bg={tableBg}
                  >
                    <Flex w="48px" h="48px" borderRadius="14px" bg="gray.100" justify="center" align="center">
                      <Icon as={LuBuilding2} boxSize="22px" color="gray.400" />
                    </Flex>
                    <Text fontSize="sm" color={subtleText} fontStyle="italic">
                      No businesses found in this market report yet.
                    </Text>
                  </VStack>
                )}
              </Box>
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}

/* ═══════════════════════════════════════════
   SETTINGS SECTION (White-Label Branding)
   ═══════════════════════════════════════════ */
function SettingsSection({
  cardBg,
  borderColor,
  subtleBorder,
  hoverBg,
  subtleText,
  headingColor,
  inputBg,
  highlightAgencyName = false,
  onHighlightDone,
}) {
  const { agencyBranding, refreshUserProfile } = useUser();
  const toast = useToast();
  const fileInputRef = useRef(null);
  const agencyNameRef = useRef(null);

  const [agencyName, setAgencyName] = useState(agencyBranding?.agency_name || '');
  const [logoPreview, setLogoPreview] = useState(agencyBranding?.logo_url || '');
  const [isSaving, setIsSaving] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [showNameTooltip, setShowNameTooltip] = useState(false);

  useEffect(() => {
    setAgencyName(agencyBranding?.agency_name || '');
    setLogoPreview(agencyBranding?.logo_url || '');
  }, [agencyBranding]);

  useEffect(() => {
    if (!highlightAgencyName) return;

    setShowNameTooltip(true);

    const scrollTimer = setTimeout(() => {
      agencyNameRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      agencyNameRef.current?.querySelector('input')?.focus();
    }, 100);

    const hideTimer = setTimeout(() => {
      setShowNameTooltip(false);
      if (onHighlightDone) onHighlightDone();
    }, 5000);

    return () => {
      clearTimeout(scrollTimer);
      clearTimeout(hideTimer);
    };
  }, [highlightAgencyName, onHighlightDone]);

  const handleLogoUpload = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file type',
        description: 'Please upload an image file (PNG, JPG, SVG, or WebP).',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Logo must be under 2 MB.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
      return;
    }

    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
      setIsDirty(true);
    };
    reader.readAsDataURL(file);
  }, [toast]);

  const handleRemoveLogo = useCallback(() => {
    setLogoPreview('');
    setIsDirty(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  const handleNameChange = useCallback((e) => {
    setAgencyName(e.target.value);
    setIsDirty(true);
  }, []);

  const handleSave = useCallback(async () => {
    setIsSaving(true);
    try {
      await updateAgencyBranding({
        agency_name: agencyName.trim(),
        logo_url: logoPreview,
      });
      await refreshUserProfile();
      setIsDirty(false);
      toast({
        title: 'Branding updated',
        description: 'Your white-label branding has been saved.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'bottom-right',
      });
    } catch (err) {
      toast({
        title: 'Failed to save',
        description: 'Could not update branding. Please try again.',
        status: 'error',
        duration: 4000,
        isClosable: true,
        position: 'bottom-right',
      });
    } finally {
      setIsSaving(false);
    }
  }, [agencyName, logoPreview, refreshUserProfile, toast]);

  const dropzoneBg = useColorModeValue('gray.50', 'gray.900');
  const dropzoneBorder = useColorModeValue('gray.300', 'gray.600');
  const dropzoneHoverBg = useColorModeValue('blue.50', 'blue.900');

  return (
    <VStack align="stretch" spacing={6}>
      <SectionHeader
        icon={LuSettings}
        title="White-Label Branding"
        subtitle="Customize the platform appearance for your agency and clients"
      />

      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={subtleBorder}
        borderRadius="14px"
        shadow="sm"
        overflow="hidden"
      >
        <CardBody py={6} px={6}>
          <VStack align="stretch" spacing={8}>
            {/* Agency Logo */}
            <Box>
              <HStack spacing={2} mb={1}>
                <Icon as={LuImage} boxSize="14px" color="blue.500" />
                <Text fontSize="sm" fontWeight="600" color={headingColor}>
                  Agency Logo
                </Text>
              </HStack>
              <Text fontSize="xs" color={subtleText} mb={4}>
                Upload your logo to replace the default Localytics logo in the sidebar.
                Recommended size: 200×200px or smaller. Max 2 MB.
              </Text>

              {logoPreview ? (
                <HStack spacing={4} align="flex-start">
                  <Flex
                    w="80px"
                    h="80px"
                    borderRadius="12px"
                    borderWidth="1px"
                    borderColor={subtleBorder}
                    bg={dropzoneBg}
                    justify="center"
                    align="center"
                    overflow="hidden"
                    flexShrink={0}
                  >
                    <Image
                      src={logoPreview}
                      alt="Agency Logo"
                      maxW="72px"
                      maxH="72px"
                      objectFit="contain"
                    />
                  </Flex>
                  <VStack align="flex-start" spacing={2}>
                    <Button
                      size="sm"
                      variant="outline"
                      borderRadius="8px"
                      fontWeight="500"
                      fontSize="xs"
                      leftIcon={<Icon as={LuUpload} boxSize="12px" />}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      Replace Logo
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      borderRadius="8px"
                      fontWeight="500"
                      fontSize="xs"
                      color="red.500"
                      leftIcon={<Icon as={LuX} boxSize="12px" />}
                      onClick={handleRemoveLogo}
                      _hover={{ bg: 'red.50' }}
                    >
                      Remove Logo
                    </Button>
                  </VStack>
                </HStack>
              ) : (
                <Flex
                  w="100%"
                  h="120px"
                  borderRadius="12px"
                  borderWidth="2px"
                  borderStyle="dashed"
                  borderColor={dropzoneBorder}
                  bg={dropzoneBg}
                  justify="center"
                  align="center"
                  cursor="pointer"
                  _hover={{ bg: dropzoneHoverBg, borderColor: 'blue.300' }}
                  transition="all 0.2s"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <VStack spacing={2}>
                    <Flex
                      w="40px"
                      h="40px"
                      borderRadius="10px"
                      bg="blue.50"
                      justify="center"
                      align="center"
                    >
                      <Icon as={LuUpload} boxSize="18px" color="blue.500" />
                    </Flex>
                    <VStack spacing={0}>
                      <Text fontSize="sm" fontWeight="500" color={headingColor}>
                        Click to upload your logo
                      </Text>
                      <Text fontSize="xs" color={subtleText}>
                        PNG, JPG, SVG, or WebP — max 2 MB
                      </Text>
                    </VStack>
                  </VStack>
                </Flex>
              )}

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleLogoUpload}
                style={{ display: 'none' }}
              />
            </Box>

            <Divider borderColor={subtleBorder} />

            {/* Agency Name */}
            <Box ref={agencyNameRef} position="relative">
              <HStack spacing={2} mb={1}>
                <Icon as={LuPenLine} boxSize="14px" color={highlightAgencyName ? 'yellow.500' : 'blue.500'} />
                <Text fontSize="sm" fontWeight="600" color={highlightAgencyName ? 'yellow.600' : headingColor}>
                  Agency Name
                </Text>
              </HStack>
              <Text fontSize="xs" color={subtleText} mb={3}>
                Set your agency name to replace "Localytics" throughout the platform.
                Leave blank to keep the default.
              </Text>
              <Box position="relative" display="inline-block" maxW="400px" w="100%">
                <Input
                  placeholder="e.g. Acme Digital Agency"
                  value={agencyName}
                  onChange={handleNameChange}
                  bg={highlightAgencyName ? 'yellow.50' : inputBg}
                  borderColor={highlightAgencyName ? 'yellow.400' : subtleBorder}
                  borderRadius="10px"
                  h="44px"
                  fontSize="sm"
                  maxW="400px"
                  w="100%"
                  boxShadow={highlightAgencyName ? '0 0 0 3px rgba(236,201,75,0.45)' : undefined}
                  _focus={{ borderColor: highlightAgencyName ? 'yellow.400' : 'blue.400', bg: cardBg }}
                  _placeholder={{ color: 'gray.400' }}
                  sx={highlightAgencyName ? {
                    transition: 'border-color 0.2s, box-shadow 0.2s, background 0.2s',
                  } : undefined}
                />
                {showNameTooltip && (
                  <Box
                    position="absolute"
                    left="calc(100% + 12px)"
                    top="50%"
                    transform="translateY(-50%)"
                    bg="yellow.400"
                    color="gray.900"
                    px={3}
                    py={2}
                    borderRadius="10px"
                    fontSize="xs"
                    fontWeight="600"
                    whiteSpace="nowrap"
                    shadow="md"
                    zIndex={10}
                    pointerEvents="none"
                    sx={{
                      animation: 'tooltipPop 0.25s cubic-bezier(0.34,1.56,0.64,1)',
                      '@keyframes tooltipPop': {
                        '0%': { opacity: 0, transform: 'translateY(-50%) scale(0.8)' },
                        '100%': { opacity: 1, transform: 'translateY(-50%) scale(1)' },
                      },
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        right: '100%',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        borderWidth: '6px',
                        borderStyle: 'solid',
                        borderColor: 'transparent yellow.400 transparent transparent',
                        borderRightColor: 'var(--chakra-colors-yellow-400)',
                      },
                    }}
                  >
                    Put your agency name in first then go back and do your first market report!
                  </Box>
                )}
              </Box>
            </Box>

            <Divider borderColor={subtleBorder} />

            {/* Preview */}
            <Box>
              <Text fontSize="xs" fontWeight="600" color={subtleText} textTransform="uppercase" letterSpacing="0.06em" mb={3}>
                Preview
              </Text>
              <Flex
                w="72px"
                bg={useColorModeValue('white', 'gray.800')}
                borderWidth="1px"
                borderColor={subtleBorder}
                borderRadius="12px"
                direction="column"
                align="center"
                py={4}
                px={2}
                shadow="sm"
              >
                <Flex
                  w="48px"
                  h="48px"
                  borderRadius="8px"
                  bg={dropzoneBg}
                  justify="center"
                  align="center"
                  overflow="hidden"
                >
                  {logoPreview ? (
                    <Image
                      src={logoPreview}
                      alt="Preview"
                      maxW="40px"
                      maxH="40px"
                      objectFit="contain"
                    />
                  ) : (
                    <Image
                      src="/output.png"
                      alt="Default Logo"
                      maxW="40px"
                      maxH="40px"
                      objectFit="contain"
                    />
                  )}
                </Flex>
              </Flex>
            </Box>

            {/* Save */}
            <HStack spacing={3}>
              <Button
                colorScheme="blue"
                size="md"
                borderRadius="10px"
                fontWeight="600"
                px={8}
                onClick={handleSave}
                isLoading={isSaving}
                isDisabled={!isDirty}
                leftIcon={<Icon as={LuCheck} />}
                _hover={{ transform: 'translateY(-1px)', shadow: 'lg' }}
                transition="all 0.15s"
              >
                Save Branding
              </Button>
              {isDirty && (
                <Text fontSize="xs" color="orange.500" fontWeight="500">
                  You have unsaved changes
                </Text>
              )}
            </HStack>
          </VStack>
        </CardBody>
      </Card>
    </VStack>
  );
}

/* ═══════════════════════════════════════════
   SCRAPE PROGRESS FEED — step-by-step pacing
   ═══════════════════════════════════════════ */
const SCRAPE_EST_MID_MS = 52.5 * 60 * 1000;

function getStableCompetitorCount(verticalId, businessCount, startTime) {
  if (businessCount > 0) return businessCount;
  const seed = `${verticalId || ''}-${startTime || ''}`;
  let hash = 0;
  for (let i = 0; i < seed.length; i += 1) {
    hash = ((hash << 5) - hash) + seed.charCodeAt(i);
    hash |= 0;
  }
  return 18 + Math.abs(hash % 13);
}

function buildScrapeProgressSteps(competitorCount) {
  return [
    { id: 'gbp', label: 'Pulling Google Business listings…', atMs: 0 },
    { id: 'competitors', label: `Found ${competitorCount} competitors…`, atMs: 4 * 60 * 1000 },
    { id: 'reviews', label: 'Analyzing review sentiment…', atMs: 12 * 60 * 1000 },
    { id: 'ads', label: 'Pulling ad data…', atMs: 22 * 60 * 1000 },
    { id: 'search', label: 'Cross-referencing search rankings…', atMs: 32 * 60 * 1000 },
    { id: 'compile', label: 'Compiling market intelligence…', atMs: 42 * 60 * 1000 },
    { id: 'finish', label: 'Finalizing your dashboard…', atMs: 50 * 60 * 1000 },
  ];
}

function ScrapeProgressFeed({ startTime, verticalId, competitorCount, compact = false }) {
  const [now, setNow] = useState(Date.now());
  const dividerColor = useColorModeValue('orange.100', 'rgba(251,146,60,0.15)');
  const feedBg = useColorModeValue('orange.50', 'rgba(251,146,60,0.08)');
  const feedBorder = useColorModeValue('orange.100', 'rgba(251,146,60,0.2)');
  const stepDoneColor = useColorModeValue('green.600', 'green.300');
  const stepActiveColor = useColorModeValue('orange.600', 'orange.300');
  const stepPendingColor = useColorModeValue('gray.400', 'gray.500');
  const messageColor = useColorModeValue('gray.600', 'gray.300');
  const progressTrackBg = useColorModeValue('orange.50', 'rgba(251,146,60,0.1)');

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(interval);
  }, []);

  const start = startTime ? new Date(startTime).getTime() : now;
  const elapsed = Math.max(0, now - start);
  const remaining = Math.max(SCRAPE_EST_MID_MS - elapsed, 0);
  const progress = Math.min((elapsed / SCRAPE_EST_MID_MS) * 100, 100);

  const minutes = Math.floor(remaining / (60 * 1000));
  const seconds = Math.floor((remaining % (60 * 1000)) / 1000);
  const countdownText = remaining > 0
    ? `~${minutes}m ${String(seconds).padStart(2, '0')}s remaining`
    : 'Finishing up…';

  const resolvedCompetitorCount = getStableCompetitorCount(verticalId, competitorCount, startTime);
  const steps = useMemo(
    () => buildScrapeProgressSteps(resolvedCompetitorCount),
    [resolvedCompetitorCount],
  );

  const activeStepIndex = steps.reduce(
    (idx, step, i) => (elapsed >= step.atMs ? i : idx),
    0,
  );

  const visibleSteps = steps.slice(0, activeStepIndex + 1);

  return (
    <Box
      mt={compact ? 2.5 : 3}
      pt={compact ? 2.5 : 3}
      borderTopWidth="1px"
      borderTopColor={dividerColor}
    >
      <HStack justify="space-between" mb={compact ? 2 : 2.5}>
        <HStack spacing={1.5}>
          <Box
            w="7px"
            h="7px"
            borderRadius="full"
            bg="orange.400"
            sx={{
              '@keyframes runningPulse': {
                '0%,100%': { opacity: 1, transform: 'scale(1)' },
                '50%': { opacity: 0.4, transform: 'scale(0.7)' },
              },
              animation: 'runningPulse 1.4s ease-in-out infinite',
            }}
          />
          <Text fontSize="11px" fontWeight="700" color="orange.500" letterSpacing="0.05em" textTransform="uppercase">
            Deep data pull in progress
          </Text>
        </HStack>
        <Text fontSize="11px" fontWeight="500" color="gray.400" fontFamily="mono">
          {countdownText}
        </Text>
      </HStack>

      <Text
        fontSize={compact ? 'xs' : 'sm'}
        color={messageColor}
        lineHeight="1.55"
        mb={compact ? 2.5 : 3}
        fontWeight="500"
      >
        {"We're pulling live data across Google Business, reviews, ads, and search — this takes about 45-60 minutes to do right."}
      </Text>

      <Box
        bg={feedBg}
        borderWidth="1px"
        borderColor={feedBorder}
        borderRadius="12px"
        px={compact ? 3 : 4}
        py={compact ? 2.5 : 3}
      >
        <VStack align="stretch" spacing={compact ? 2 : 2.5}>
          {visibleSteps.map((step, index) => {
            const isComplete = index < activeStepIndex;
            const isActive = index === activeStepIndex;

            return (
              <HStack
                key={step.id}
                spacing={2.5}
                align="flex-start"
                sx={{
                  '@keyframes stepFadeIn': {
                    from: { opacity: 0, transform: 'translateY(4px)' },
                    to: { opacity: 1, transform: 'translateY(0)' },
                  },
                  animation: isActive ? 'stepFadeIn 0.45s ease-out' : undefined,
                }}
              >
                <Flex
                  w="18px"
                  h="18px"
                  justify="center"
                  align="center"
                  flexShrink={0}
                  mt="1px"
                >
                  {isComplete ? (
                    <Icon as={LuCheckCircle2} boxSize="16px" color={stepDoneColor} />
                  ) : isActive ? (
                    <Icon
                      as={LuLoader}
                      boxSize="14px"
                      color={stepActiveColor}
                      sx={{
                        '@keyframes spin': {
                          from: { transform: 'rotate(0deg)' },
                          to: { transform: 'rotate(360deg)' },
                        },
                        animation: 'spin 1s linear infinite',
                      }}
                    />
                  ) : (
                    <Box w="6px" h="6px" borderRadius="full" bg={stepPendingColor} />
                  )}
                </Flex>
                <Text
                  fontSize={compact ? '11px' : 'xs'}
                  fontWeight={isActive ? '600' : '500'}
                  color={isComplete ? stepDoneColor : isActive ? stepActiveColor : stepPendingColor}
                  lineHeight="1.45"
                >
                  {step.label}
                </Text>
              </HStack>
            );
          })}
        </VStack>
      </Box>

      <HStack justify="space-between" mt={2} px={0.5}>
        <Text fontSize="10px" color="orange.400" fontWeight="500">
          Est. 45–60 min deep market report
        </Text>
        <Text fontSize="10px" color="orange.500" fontWeight="700">
          Step {activeStepIndex + 1} of {steps.length}
        </Text>
      </HStack>

      <Box mt={1.5} h={compact ? '4px' : '5px'} bg={progressTrackBg} borderRadius="full" overflow="hidden">
        <Box
          h="100%"
          w={`${progress}%`}
          bgGradient="linear(to-r, orange.300, orange.500)"
          borderRadius="full"
          transition="width 1s ease-out"
        />
      </Box>
    </Box>
  );
}

/* ═══════════════════════════════════════════
   SCRAPES SECTION
   ═══════════════════════════════════════════ */
function ScrapesSection({
  scrapes,
  allScrapes,
  clients,
  currentScrape,
  activeVertical,
  setActiveVertical,
  scrapeSearch,
  setScrapeSearch,
  scrapeStatuses = [],
  onCreateScrape,
  onKeywordScrape,
  onUpgrade,
  onViewDetails,
  cardBg,
  borderColor,
  subtleBorder,
  hoverBg,
  subtleText,
  headingColor,
  activeBg,
  activeBorder,
}) {
  const usedCount = allScrapes.length;

  /** Get the latest scrape status for a vertical */
  const getScrapeStatusForVertical = useCallback((verticalId) => {
    const matches = scrapeStatuses.filter(ex => (ex.vertical_slug || ex.vertical) === verticalId);
    if (matches.length === 0) return null;
    matches.sort((a, b) => (b.start_time || '').localeCompare(a.start_time || ''));
    return matches[0];
  }, [scrapeStatuses]);

  const sortedScrapes = useMemo(() => {
    const activeIdx = scrapes.findIndex(s =>
      s.id === currentScrape || s.name?.toLowerCase() === currentScrape?.toLowerCase()
    );
    if (activeIdx <= 0) return scrapes;
    const active = scrapes[activeIdx];
    const rest = scrapes.filter((_, i) => i !== activeIdx);
    return [active, ...rest];
  }, [scrapes, currentScrape]);
  const freeScrapeSlots = Math.max(0, FREE_SCRAPES - usedCount);
  const lockedScrapeSlots = Math.max(0, TOTAL_PLAN_SCRAPES - usedCount - freeScrapeSlots);

  const progressColor = useColorModeValue('blue.500', 'blue.300');
  const progressTrack = useColorModeValue('gray.100', 'gray.700');
  const freeSlotBg = useColorModeValue('blue.50', 'blue.900');
  const freeSlotHoverBg = useColorModeValue('blue.100', 'blue.800');
  const lockedBg = useColorModeValue('gray.50', 'gray.800');
  const lockedBorderColor = useColorModeValue('gray.200', 'gray.600');
  const lockedIconBg = useColorModeValue('gray.100', 'gray.700');

  return (
    <VStack align="stretch" spacing={6}>
      <SectionHeader
        icon={LuDatabase}
        title="Market Reports Management"
        subtitle="Track markets and manage business data collections"
        action={
          <HStack spacing={2}>
            {freeScrapeSlots > 0 ? (
              <Button
                colorScheme="blue"
                size="sm"
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                leftIcon={<Icon as={LuPlus} boxSize="14px" />}
                onClick={onCreateScrape}
                _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
                transition="all 0.15s"
                px={5}
              >
                Use Free Market Report
              </Button>
            ) : (
              <Button
                colorScheme="blue"
                size="sm"
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                leftIcon={<Icon as={LuRocket} boxSize="14px" />}
                onClick={onUpgrade}
                _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
                transition="all 0.15s"
                px={5}
              >
                Upgrade Plan
              </Button>
            )}
          </HStack>
        }
      />

      {/* ─── Free Scrape Progress ─── */}
      <Card
        bg={cardBg}
        borderWidth="1px"
        borderColor={subtleBorder}
        borderRadius="14px"
        shadow="sm"
        overflow="hidden"
      >
        <CardBody py={4} px={5}>
          <Flex justify="space-between" align="center" mb={2.5}>
            <HStack spacing={2}>
              <Icon as={LuSparkles} boxSize="14px" color="blue.500" />
              <Text fontSize="sm" fontWeight="600" color={headingColor}>
                Free Market Reports
              </Text>
              <Badge
                bg="blue.50"
                color="blue.600"
                fontSize="10px"
                borderRadius="full"
                px={2}
                fontWeight="700"
              >
                {usedCount} of {FREE_SCRAPES} used
              </Badge>
            </HStack>
            <Text fontSize="xs" color={subtleText} fontWeight="500">
              {TOTAL_PLAN_SCRAPES} total capacity
            </Text>
          </Flex>
          <Box
            w="100%"
            h="6px"
            bg={progressTrack}
            borderRadius="full"
            overflow="hidden"
          >
            <Box
              h="100%"
              w={`${(usedCount / TOTAL_PLAN_SCRAPES) * 100}%`}
              bg={progressColor}
              borderRadius="full"
              transition="width 0.4s ease"
            />
          </Box>
          {freeScrapeSlots > 0 && (
            <Text fontSize="xs" color="blue.500" fontWeight="500" mt={2}>
              You have {freeScrapeSlots} free {freeScrapeSlots === 1 ? 'market report' : 'market reports'} remaining — use {freeScrapeSlots === 1 ? 'it' : 'them'} to explore new markets!
            </Text>
          )}
        </CardBody>
      </Card>

      <InputGroup maxW="400px">
        <InputLeftElement pointerEvents="none" h="40px">
          <Icon as={LuSearch} color="gray.400" boxSize="15px" />
        </InputLeftElement>
        <Input
          placeholder="Search by name, niche, or region..."
          value={scrapeSearch}
          onChange={(e) => setScrapeSearch(e.target.value)}
          bg={cardBg}
          borderColor={subtleBorder}
          borderRadius="10px"
          h="40px"
          fontSize="sm"
          _focus={{ borderColor: 'blue.400', shadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
          _placeholder={{ color: 'gray.400' }}
        />
      </InputGroup>

      <VStack align="stretch" spacing={3}>
        {/* ─── Assigned Market Report (dynamic — follows admin's current assignment) ─── */}
        {sortedScrapes.map((scrape, index) => {
          const isCurrent = scrape.id === currentScrape || scrape.name?.toLowerCase() === currentScrape?.toLowerCase();
          const assignedClients = clients.filter(c => c.assignedScrape === scrape.id);
          const latestStatus = getScrapeStatusForVertical(scrape.id);
          const statusColor = latestStatus?.status === 'RUNNING' ? 'orange'
            : latestStatus?.status === 'SUCCEEDED' ? 'green'
            : latestStatus?.status === 'FAILED' ? 'red'
            : 'gray';

          return (
            <Card
              key={scrape.id}
              bg={isCurrent ? activeBg : cardBg}
              borderWidth="1px"
              borderColor={isCurrent ? activeBorder : subtleBorder}
              borderRadius="14px"
              shadow="sm"
              _hover={{
                shadow: 'md',
                borderColor: 'blue.200',
                transform: 'translateY(-1px)',
              }}
              transition="all 0.2s"
              cursor={isCurrent ? 'default' : 'pointer'}
              overflow="hidden"
              position="relative"
              onClick={!isCurrent ? () => setActiveVertical(scrape.id) : undefined}
            >
              {isCurrent && (
                <Box
                  position="absolute"
                  top={0}
                  left={0}
                  bottom={0}
                  w="3px"
                  bg="blue.400"
                  borderLeftRadius="14px"
                />
              )}
              <CardBody py={5} px={6}>
                <Flex justify="space-between" align="center">
                  <HStack spacing={4} flex={1}>
                    <Flex
                      w="44px"
                      h="44px"
                      borderRadius="12px"
                      bg={isCurrent ? 'blue.50' : hoverBg}
                      justify="center"
                      align="center"
                      flexShrink={0}
                    >
                      <Icon
                        as={LuDatabase}
                        boxSize="18px"
                        color={isCurrent ? 'blue.500' : 'gray.400'}
                      />
                    </Flex>
                    <Box flex={1}>
                      <HStack spacing={2} align="center" mb={1}>
                        <Text
                          fontSize="sm"
                          fontWeight="700"
                          textTransform="capitalize"
                          color={headingColor}
                          letterSpacing="-0.01em"
                        >
                          {scrape.name || scrape.id}
                        </Text>
                        {isCurrent && (
                          <Badge
                            bg="blue.50"
                            color="blue.600"
                            fontSize="9px"
                            borderRadius="full"
                            px={2}
                            fontWeight="700"
                            textTransform="uppercase"
                            letterSpacing="0.04em"
                          >
                            Assigned
                          </Badge>
                        )}
                        {scrape.niche && (
                          <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full">
                            <TagLabel fontSize="xs" fontWeight="500">{scrape.niche}</TagLabel>
                          </Tag>
                        )}
                        {latestStatus && (
                          latestStatus.status === 'SUCCEEDED' ? (
                            <Box
                              as="span"
                              display="inline-flex"
                              alignItems="center"
                              fontSize="9px"
                              borderRadius="full"
                              px={2.5}
                              py="2px"
                              fontWeight="800"
                              textTransform="uppercase"
                              letterSpacing="0.06em"
                              bgGradient="linear(135deg, #4ade80, #16a34a, #15803d)"
                              color="white"
                              boxShadow="0 2px 10px rgba(22,163,74,0.45), 0 1px 4px rgba(0,0,0,0.12), inset 0 1px 0 rgba(255,255,255,0.25)"
                              sx={{
                                textShadow: '0 1px 2px rgba(0,0,0,0.18)',
                                backdropFilter: 'blur(4px)',
                              }}
                            >
                              SUCCEEDED
                            </Box>
                          ) : (
                            <Badge
                              bg={`${statusColor}.50`}
                              color={`${statusColor}.600`}
                              fontSize="9px"
                              borderRadius="full"
                              px={2}
                              fontWeight="700"
                              textTransform="uppercase"
                              letterSpacing="0.04em"
                            >
                              {latestStatus.status}
                            </Badge>
                          )
                        )}
                      </HStack>
                      <HStack spacing={4} mt={0.5}>
                        {scrape.region && (
                          <HStack spacing={1.5}>
                            <Icon as={LuMapPin} boxSize="12px" color={subtleText} />
                            <Text fontSize="xs" color={subtleText} fontWeight="500">{scrape.region}</Text>
                          </HStack>
                        )}
                        {scrape.businessCount != null && (
                          <HStack spacing={1.5}>
                            <Icon as={LuBuilding2} boxSize="12px" color={subtleText} />
                            <Text fontSize="xs" color={subtleText} fontWeight="500">
                              {scrape.businessCount} businesses
                            </Text>
                          </HStack>
                        )}
                        <HStack spacing={1.5}>
                          <Icon as={LuUsers} boxSize="12px" color={subtleText} />
                          <Text fontSize="xs" color={subtleText} fontWeight="500">
                            {assignedClients.length} {assignedClients.length === 1 ? 'client' : 'clients'}
                          </Text>
                        </HStack>
                      </HStack>
                    </Box>
                  </HStack>

                  <HStack spacing={4}>
                    {assignedClients.length > 0 && (
                      <Wrap spacing={1}>
                        {assignedClients.slice(0, 3).map(c => (
                          <WrapItem key={c.id}>
                            <Tag size="sm" variant="subtle" colorScheme="blue" borderRadius="full" px={2}>
                              <TagLabel fontSize="xs" fontWeight="500">{c.name.split(' ')[0]}</TagLabel>
                            </Tag>
                          </WrapItem>
                        ))}
                        {assignedClients.length > 3 && (
                          <WrapItem>
                            <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full" px={2}>
                              <TagLabel fontSize="xs" fontWeight="500">+{assignedClients.length - 3}</TagLabel>
                            </Tag>
                          </WrapItem>
                        )}
                      </Wrap>
                    )}
                    <Tooltip label="View details" placement="top" hasArrow>
                      <IconButton
                        icon={<Icon as={LuChevronRight} />}
                        size="sm"
                        variant="ghost"
                        borderRadius="8px"
                        color={subtleText}
                        _hover={{ bg: 'blue.50', color: 'blue.500' }}
                        aria-label="View market report details"
                        onClick={(e) => { e.stopPropagation(); onViewDetails && onViewDetails(scrape); }}
                      />
                    </Tooltip>
                  </HStack>
                </Flex>
                {latestStatus?.status === 'RUNNING' && (
                  <ScrapeProgressFeed
                    startTime={latestStatus?.start_time}
                    verticalId={scrape.id}
                    competitorCount={scrape.businessCount}
                    compact
                  />
                )}
              </CardBody>
            </Card>
          );
        })}

        {/* ─── Available Free Market Report Slots ─── */}
        {freeScrapeSlots > 0 && Array.from({ length: freeScrapeSlots }).map((_, i) => (
          <Card
            key={`free-slot-${i}`}
            bg={freeSlotBg}
            borderWidth="2px"
            borderStyle="dashed"
            borderColor="blue.300"
            borderRadius="14px"
            shadow="none"
            cursor="pointer"
            onClick={onCreateScrape}
            _hover={{
              bg: freeSlotHoverBg,
              borderColor: 'blue.400',
              shadow: 'md',
              transform: 'translateY(-2px)',
            }}
            transition="all 0.2s"
            overflow="hidden"
            role="button"
            aria-label="Create a free market report"
          >
            <CardBody py={5} px={6}>
              <Flex justify="space-between" align="center">
                <HStack spacing={4} flex={1}>
                  <Flex
                    w="44px"
                    h="44px"
                    borderRadius="12px"
                    bg="blue.100"
                    justify="center"
                    align="center"
                    flexShrink={0}
                  >
                    <Icon as={LuPlus} boxSize="20px" color="blue.500" strokeWidth={2.5} />
                  </Flex>
                  <Box flex={1}>
                    <HStack spacing={2} align="center" mb={0.5}>
                      <Text
                        fontSize="sm"
                        fontWeight="700"
                        color="blue.600"
                        letterSpacing="-0.01em"
                      >
                        Use Free Market Report
                      </Text>
                      <Badge
                        bg="blue.100"
                        color="blue.700"
                        fontSize="9px"
                        borderRadius="full"
                        px={2}
                        fontWeight="700"
                        textTransform="uppercase"
                        letterSpacing="0.04em"
                      >
                        Free
                      </Badge>
                    </HStack>
                    <Text fontSize="xs" color="blue.500" fontWeight="500">
                      Tap to discover a new market — no cost, results in 45–60 minutes
                    </Text>
                  </Box>
                </HStack>
                <Flex
                  w="36px"
                  h="36px"
                  borderRadius="full"
                  bg="blue.500"
                  justify="center"
                  align="center"
                  flexShrink={0}
                  _groupHover={{ bg: 'blue.600' }}
                >
                  <Icon as={LuPlus} boxSize="16px" color="white" strokeWidth={3} />
                </Flex>
              </Flex>
            </CardBody>
          </Card>
        ))}

        {/* ─── Locked Market Report Slots ─── */}
        {lockedScrapeSlots > 0 && Array.from({ length: Math.min(lockedScrapeSlots, TOTAL_PLAN_SCRAPES - FREE_SCRAPES) }).map((_, i) => (
          <Box
            key={`locked-slot-${i}`}
            borderWidth="1.5px"
            borderStyle="dashed"
            borderColor={lockedBorderColor}
            borderRadius="14px"
            bg={lockedBg}
            opacity={0.55}
            py={4}
            px={6}
            transition="all 0.2s"
            _hover={{ opacity: 0.7 }}
          >
            <Flex justify="space-between" align="center">
              <HStack spacing={4}>
                <Flex
                  w="44px"
                  h="44px"
                  borderRadius="12px"
                  bg={lockedIconBg}
                  justify="center"
                  align="center"
                  flexShrink={0}
                >
                  <Icon as={LuLock} boxSize="16px" color="gray.400" />
                </Flex>
                <Box>
                  <Text fontSize="sm" fontWeight="600" color="gray.400">
                    Market Report Slot {usedCount + freeScrapeSlots + i + 1}
                  </Text>
                  <Text fontSize="xs" color="gray.400" fontWeight="400">
                    Upgrade your plan to unlock
                  </Text>
                </Box>
              </HStack>
              <Icon as={LuLock} boxSize="14px" color="gray.300" />
            </Flex>
          </Box>
        ))}

        {scrapes.length === 0 && freeScrapeSlots === 0 && (
          <Flex justify="center" py={16}>
            <VStack spacing={3}>
              <Flex
                w="56px"
                h="56px"
                borderRadius="16px"
                bg="gray.100"
                justify="center"
                align="center"
              >
                <Icon as={LuSearch} boxSize={6} color="gray.300" />
              </Flex>
              <Text color="gray.400" fontSize="sm" fontWeight="500">
                No market reports match your search
              </Text>
            </VStack>
          </Flex>
        )}
      </VStack>
    </VStack>
  );
}

/* ═══════════════════════════════════════════
   CLIENTS SECTION
   ═══════════════════════════════════════════ */
function ClientsSection({
  clients,
  allClients,
  scrapes,
  clientSearch,
  setClientSearch,
  onClientClick,
  onAddClient,
  cardBg,
  subtleBorder,
  subtleText,
  headingColor,
}) {

  return (
    <VStack align="stretch" spacing={6}>
      <SectionHeader
        icon={LuUsers}
        title="Clients"
        subtitle="Manage your clients and their market report assignments"
        action={
          <Button
            colorScheme="blue"
            size="sm"
            borderRadius="10px"
            fontWeight="600"
            fontSize="13px"
            leftIcon={<Icon as={LuUserPlus} boxSize="14px" />}
            onClick={onAddClient}
            _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
            transition="all 0.15s"
            px={5}
          >
            Add Client
          </Button>
        }
      />

      {(allClients.length > 0) && (
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none" h="40px">
            <Icon as={LuSearch} color="gray.400" boxSize="15px" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or business..."
            value={clientSearch}
            onChange={(e) => setClientSearch(e.target.value)}
            bg={cardBg}
            borderColor={subtleBorder}
            borderRadius="10px"
            h="40px"
            fontSize="sm"
            _focus={{ borderColor: 'blue.400', shadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
            _placeholder={{ color: 'gray.400' }}
          />
        </InputGroup>
      )}

      {allClients.length === 0 ? (
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={subtleBorder}
          borderRadius="14px"
          shadow="sm"
        >
          <CardBody py={12}>
            <VStack spacing={4}>
              <Flex
                w="60px"
                h="60px"
                borderRadius="16px"
                bg="blue.50"
                justify="center"
                align="center"
              >
                <Icon as={LuUsers} boxSize="26px" color="blue.400" />
              </Flex>
              <VStack spacing={1}>
                <Text fontSize="sm" fontWeight="700" color={headingColor}>
                  No clients yet
                </Text>
                <Text fontSize="xs" color={subtleText} textAlign="center" maxW="260px">
                  Add your first client to assign them a market vertical and give them personalized dashboard access.
                </Text>
              </VStack>
              <Button
                colorScheme="blue"
                size="sm"
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                leftIcon={<Icon as={LuUserPlus} boxSize="14px" />}
                onClick={onAddClient}
                mt={1}
              >
                Add Your First Client
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : clients.length === 0 ? (
        <Flex justify="center" py={10}>
          <VStack spacing={2}>
            <Icon as={LuSearch} boxSize={6} color="gray.300" />
            <Text color="gray.400" fontSize="sm" fontWeight="500">
              No clients match your search
            </Text>
          </VStack>
        </Flex>
      ) : (
        <VStack align="stretch" spacing={3}>
          {clients.map((client) => {
            const scrapeInfo = scrapes.find(s => s.id === client.assignedScrape);

            return (
              <Card
                key={client.id}
                bg={cardBg}
                borderWidth="1px"
                borderColor={subtleBorder}
                borderRadius="14px"
                shadow="sm"
                cursor="pointer"
                _hover={{
                  shadow: 'md',
                  borderColor: 'blue.200',
                  transform: 'translateY(-1px)',
                }}
                transition="all 0.2s"
                onClick={() => onClientClick(client)}
                overflow="hidden"
              >
                <CardBody py={5} px={6}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={4} flex={1}>
                      <Avatar
                        size="md"
                        name={client.name}
                        bg="blue.500"
                        color="white"
                      />
                      <Box flex={1}>
                        <HStack spacing={2} align="center" mb={0.5}>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            color={headingColor}
                            letterSpacing="-0.01em"
                          >
                            {client.name}
                          </Text>
                        </HStack>
                        {client.businessName && (
                          <HStack spacing={1.5} mb={1}>
                            <Icon as={LuBuilding2} boxSize="11px" color={subtleText} />
                            <Text fontSize="xs" color={subtleText} fontWeight="500">
                              {client.businessName}
                            </Text>
                          </HStack>
                        )}
                        <Text fontSize="xs" color={subtleText} fontWeight="400" mb={2}>
                          {client.email}
                        </Text>
                        {client.assignedScrape ? (
                          <Tag
                            size="sm"
                            variant="subtle"
                            colorScheme="blue"
                            borderRadius="full"
                            px={2.5}
                          >
                            <TagLeftIcon as={LuDatabase} boxSize="10px" />
                            <TagLabel fontSize="xs" fontWeight="500" textTransform="capitalize">
                              {scrapeInfo?.name || client.assignedScrape}
                            </TagLabel>
                            {scrapeInfo?.region && (
                              <Text as="span" fontSize="xs" color="blue.400" ml={1}>
                                &middot; {scrapeInfo.region}
                              </Text>
                            )}
                          </Tag>
                        ) : (
                          <Text fontSize="xs" color="gray.400" fontStyle="italic">
                            No market report assigned
                          </Text>
                        )}
                      </Box>
                    </HStack>

                    <HStack spacing={2}>
                      <Icon as={LuChevronRight} boxSize="16px" color={subtleText} />
                    </HStack>
                  </Flex>
                </CardBody>
              </Card>
            );
          })}
        </VStack>
      )}

      {/* ─── Incentive banner — grow your client base ─── */}
      <Box
        borderWidth="1.5px"
        borderStyle="dashed"
        borderColor="blue.200"
        borderRadius="14px"
        bg="blue.50"
        p={5}
        cursor="pointer"
        onClick={onAddClient}
        role="button"
        _hover={{ bg: 'blue.100', borderColor: 'blue.300' }}
        transition="all 0.2s"
      >
        <Flex justify="space-between" align="center">
          <HStack spacing={4}>
            <Flex
              w="44px"
              h="44px"
              borderRadius="12px"
              bg="white"
              justify="center"
              align="center"
              flexShrink={0}
              shadow="sm"
            >
              <Icon as={LuRocket} boxSize="18px" color="blue.500" />
            </Flex>
            <Box>
              <Text fontSize="sm" fontWeight="700" color="blue.700" mb={0.5}>
                Grow your client base
              </Text>
              <Text fontSize="xs" color="blue.600">
                Add clients to assign market verticals and give them tailored dashboard access
              </Text>
            </Box>
          </HStack>
          <Flex
            w="32px"
            h="32px"
            borderRadius="full"
            bg="blue.500"
            justify="center"
            align="center"
            flexShrink={0}
          >
            <Icon as={LuPlus} boxSize="14px" color="white" strokeWidth={2.5} />
          </Flex>
        </Flex>
      </Box>
    </VStack>
  );
}

/* ═══════════════════════════════════════════
   MEMBERS SECTION
   ═══════════════════════════════════════════ */
function MembersSection({
  members,
  allMembers,
  memberSearch,
  setMemberSearch,
  onAddMember,
  onDeleteMember,
  ownerEmail,
  isLoading,
  cardBg,
  borderColor,
  subtleBorder,
  hoverBg,
  subtleText,
  headingColor,
}) {
  return (
    <VStack align="stretch" spacing={6}>
      <SectionHeader
        icon={LuUsers}
        title="Members"
        subtitle="Manage your agency team members"
        action={
          <Button
            colorScheme="blue"
            size="sm"
            borderRadius="10px"
            fontWeight="600"
            fontSize="13px"
            leftIcon={<Icon as={LuUserPlus} boxSize="14px" />}
            onClick={onAddMember}
            _hover={{ transform: 'translateY(-1px)', shadow: 'md' }}
            transition="all 0.15s"
            px={5}
          >
            Add Member
          </Button>
        }
      />

      {(allMembers.length > 0) && (
        <InputGroup maxW="400px">
          <InputLeftElement pointerEvents="none" h="40px">
            <Icon as={LuSearch} color="gray.400" boxSize="15px" />
          </InputLeftElement>
          <Input
            placeholder="Search by name, email, or vertical..."
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            bg={cardBg}
            borderColor={subtleBorder}
            borderRadius="10px"
            h="40px"
            fontSize="sm"
            _focus={{ borderColor: 'blue.400', shadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
            _placeholder={{ color: 'gray.400' }}
          />
        </InputGroup>
      )}

      {isLoading ? (
        <VStack align="stretch" spacing={3}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} h="80px" borderRadius="14px" />
          ))}
        </VStack>
      ) : members.length === 0 ? (
        <Card
          bg={cardBg}
          borderWidth="1px"
          borderColor={subtleBorder}
          borderRadius="14px"
          shadow="sm"
        >
          <CardBody py={10}>
            <VStack spacing={3}>
              <Flex
                w="48px"
                h="48px"
                borderRadius="12px"
                bg="gray.100"
                justify="center"
                align="center"
              >
                <Icon as={LuUsers} boxSize="20px" color="gray.400" />
              </Flex>
              <VStack spacing={1}>
                <Text fontSize="sm" fontWeight="600" color={headingColor}>
                  No members yet
                </Text>
                <Text fontSize="xs" color={subtleText} textAlign="center">
                  Add team members to give them access to your agency's verticals.
                </Text>
              </VStack>
              <Button
                colorScheme="blue"
                size="sm"
                borderRadius="10px"
                fontWeight="600"
                fontSize="13px"
                leftIcon={<Icon as={LuUserPlus} boxSize="14px" />}
                onClick={onAddMember}
                mt={2}
              >
                Add Member
              </Button>
            </VStack>
          </CardBody>
        </Card>
      ) : (
        <VStack align="stretch" spacing={3}>
          {members.map((member) => {
            const isOwner = member.email === ownerEmail;
            const fullName = [member.first_name, member.last_name].filter(Boolean).join(' ');

            return (
              <Card
                key={member.email}
                bg={cardBg}
                borderWidth="1px"
                borderColor={isOwner ? 'blue.200' : subtleBorder}
                borderRadius="14px"
                shadow="sm"
                overflow="hidden"
                position="relative"
              >
                {isOwner && (
                  <Box
                    position="absolute"
                    top={0}
                    left={0}
                    bottom={0}
                    w="3px"
                    bg="blue.400"
                    borderLeftRadius="14px"
                  />
                )}
                <CardBody py={5} px={6}>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={4} flex={1}>
                      <Avatar
                        size="md"
                        name={fullName || member.email}
                        bg={isOwner ? 'blue.500' : 'gray.500'}
                        color="white"
                      />
                      <Box flex={1}>
                        <HStack spacing={2} align="center" mb={0.5}>
                          <Text
                            fontSize="sm"
                            fontWeight="700"
                            color={headingColor}
                            letterSpacing="-0.01em"
                          >
                            {fullName || member.email}
                          </Text>
                          {isOwner && (
                            <Badge
                              bg="blue.50"
                              color="blue.600"
                              fontSize="9px"
                              borderRadius="full"
                              px={2}
                              fontWeight="700"
                              textTransform="uppercase"
                              letterSpacing="0.04em"
                            >
                              You
                            </Badge>
                          )}
                          {member.role === 'owner' && (
                            <Badge
                              bg="purple.50"
                              color="purple.600"
                              fontSize="9px"
                              borderRadius="full"
                              px={2}
                              fontWeight="700"
                              textTransform="uppercase"
                              letterSpacing="0.04em"
                            >
                              Owner
                            </Badge>
                          )}
                        </HStack>
                        <Text fontSize="xs" color={subtleText} fontWeight="400" mb={2}>
                          {member.email}
                        </Text>
                        <Wrap spacing={2}>
                          {member.vertical && (
                            <WrapItem>
                              <Tag size="sm" variant="subtle" colorScheme="blue" borderRadius="full" px={2.5}>
                                <TagLeftIcon as={LuDatabase} boxSize="10px" />
                                <TagLabel fontSize="xs" fontWeight="500" textTransform="capitalize">
                                  {member.vertical}
                                </TagLabel>
                              </Tag>
                            </WrapItem>
                          )}
                          {member.tier && (
                            <WrapItem>
                              <Tag size="sm" variant="subtle" colorScheme="gray" borderRadius="full" px={2.5}>
                                <TagLabel fontSize="xs" fontWeight="500">
                                  {member.tier}
                                </TagLabel>
                              </Tag>
                            </WrapItem>
                          )}
                        </Wrap>
                      </Box>
                    </HStack>

                    {!isOwner && (
                      <Tooltip label="Remove member" fontSize="xs">
                        <IconButton
                          variant="ghost"
                          size="sm"
                          borderRadius="8px"
                          icon={<Icon as={LuTrash2} boxSize="15px" />}
                          color="gray.400"
                          _hover={{ bg: 'red.50', color: 'red.500' }}
                          onClick={() => onDeleteMember(member)}
                          aria-label="Remove member"
                        />
                      </Tooltip>
                    )}
                  </Flex>
                </CardBody>
              </Card>
            );
          })}
        </VStack>
      )}

      {/* ─── Incentive banner — scale your team ─── */}
      {!isLoading && (
        <Box
          borderWidth="1.5px"
          borderStyle="dashed"
          borderColor="blue.200"
          borderRadius="14px"
          bg="blue.50"
          p={5}
          cursor="pointer"
          onClick={onAddMember}
          role="button"
          _hover={{ bg: 'blue.100', borderColor: 'blue.300' }}
          transition="all 0.2s"
        >
          <Flex justify="space-between" align="center">
            <HStack spacing={4}>
              <Flex
                w="44px"
                h="44px"
                borderRadius="12px"
                bg="white"
                justify="center"
                align="center"
                flexShrink={0}
                shadow="sm"
              >
                <Icon as={LuZap} boxSize="18px" color="blue.500" />
              </Flex>
              <Box>
                <Text fontSize="sm" fontWeight="700" color="blue.700" mb={0.5}>
                  Scale your team
                </Text>
                <Text fontSize="xs" color="blue.600">
                  Invite team members to collaborate on market data and manage client verticals
                </Text>
              </Box>
            </HStack>
            <Flex
              w="32px"
              h="32px"
              borderRadius="full"
              bg="blue.500"
              justify="center"
              align="center"
              flexShrink={0}
            >
              <Icon as={LuPlus} boxSize="14px" color="white" strokeWidth={2.5} />
            </Flex>
          </Flex>
        </Box>
      )}
    </VStack>
  );
}

export default AgencyAdminDashboard;
